import { ComponentType, createContext, FC, PropsWithChildren, PureComponent, useContext, useEffect, useReducer, useRef, } from "react";
import {
  ComparingZipAnalysisQueryParameters,
  SpecialtyLevelResults,
  ZipAnalysisQueryParameters,
  ZipClassTableData,
  ZipCodeTableData,
  ZipContextEventHandlersObject,
  ZipContextInProps,
  ZipContextOutProps,
  ZipProjectActionsEnum,
  ZipProjectModuleRoutesEnum,
  ZipCodeFilterParams,
  ZipDetailsApiResponse
} from "./interfaces";
import {
  AttestationRateDays,
  ChangedGapsEnum,
  NetworkAssessmentAnalysisType,
  ProjectionProviderType,
  ProjectPerspective, ProjectRunConfigurationStatus,
  ProjectRunConfigurationType,
  ProjectRunStatusEnum,
  ProjectRunStatusTypeEnum,
  SpecialtyTypeEnum,
  StatusSeverity,
  TimeFrequency
} from "@app/projects/enumerators";
import { zipProjectReducer } from "@app/projects/zip-projects/zipProjectReducer";
import ZipProjectService from "@app/projects/zipProjectService";
import ProjectService from "@app/projects/projectService";
import Project from "@app/projects/project";
import ProjectRunConfigurationService from "@app/projects/projectRunConfigurationService";
import { SortDirections } from "@app/shared/waffles";
import { useHistory, useRouteMatch } from "react-router-dom";
import ProjectRunConfiguration from "@app/projects/projectRunConfiguration";
import { ProcessingSpecialtyGroupExceptionsPayload, ZipCodeAnalysisStatusChangeNotificationMessagePayload, ZipCodeAnalysisNcpStatusChangeNotificationMessagePayload } from "@app/notifications/message";
import { ProjectModule } from "@app/projects/literals";
import {
  getTopAndBottomRunConfigurations,
  getSortedColumnFromEnum,
  haveFilterSelectionsChanged,
  isProjectRunning,
  translateUrlModuleToProjectModule,
  getSpecialtyExceptionKey,
  getUrlSearchString,
  getRunConfigurationsFromSearchParams,
  getSearchParametersFromFilterSelections
} from "@app/projects/zip-projects/utility";
import { AdequacyFilters } from "@app/projects/network-assessment/adequacy";
import { CalculationMethodEnum } from "@app/templates/templateModels";
import { ZipCodeDisplayModel } from "@app/projects/zip-projects/interfaces/zipCodeDisplayModel";
import { ZipClassAggregateResultDisplayModel } from "@app/projects/zip-projects/interfaces/zipClassAggregateResultDisplayModel";
import { ZipAnalysisSortOption } from "@app/projects/zip-projects/interfaces/zipAnalysisSortOption";
import { ZipAnalysisSearchSelections } from "@app/projects/zip-projects/interfaces/zipAnalysisSearchSelections";
import { ZipAnalysisSearchParameters } from "@app/projects/zip-projects/interfaces/zipAnalysisSearchParameters";
import { ZipAnalysisSearchOptions } from "@app/projects/zip-projects/interfaces/zipAnalysisSearchOptions";
import { ZipContextState } from "@app/projects/zip-projects/interfaces/zipContextState";
import { ChangePerspectiveAction } from "@app/projects/zip-projects/interfaces/actions/changePerspectiveAction";
import { TimesliceExceptions } from "@app/projects/zip-projects/interfaces/specialtyGroupExceptionsMap";
import ExceptionsService from "@app/exceptions/exceptionsService";
import { SpecialtyGroupException } from "@app/exceptions/interfaces";
import { ZipClassTrendChartItem, ZipCodeTrendChartItem } from "@app/projects/zip-projects/interfaces/zipTrendChartItems";
import { TrendChartItemType } from "@app/velvet/enumerators";
import { FeatureFlagContext } from "@app/feature-flags/FeatureFlagContext";
import Features from "@app/feature-flags/features";
import { DropdownItem } from "@app/shared/checkbox-dropdown/types";
import { parseQuerystring } from "@app/shared/utility/parseQuerystring";
import { querystringify } from "@app/shared/utility/querystringify";
import Roles from "@app/user/roles";
import TemplateService from "@app/templates/templateService";
import { ProjectRunStatusObject } from "@app/projects/projectRunStatusObject";
import { setLastVisitedZipModule } from "@app/projects/zip-projects/state-management/lastVisitedZipModule";
import { setLastAppliedMembership } from "@app/projects/network-assessment/network-integrity/NetworkIntegrityContext";

const initialFilterSelections: ZipAnalysisSearchSelections = {
  city: "",
  county: {
    display: "All Counties",
    key: "all",
    name: "All Counties",
  },
  metOverall: {
    key: "all",
    name: "All",
  },
  metPassPercent: {
    key: "all",
    name: "All",
  },
  specialtyGroupCode: {
    display: "All Specialty Groups",
    key: "all",
    name: "All Specialty Groups",
  },
  state: {
    display: "All States",
    key: "all",
    name: "All States",
  },
  zipClass: {
    display: "All Classifications",
    key: "all",
    name: "All Classifications",
  },
  zipCode: "",
};

const initialValues: ZipContextOutProps = {
  dispatch: null,
  getLatestRunConfiguration: () => null,
  getSearchParametersFromFilterSelections: () => null,
  onApplyFilters: null,
  onChangeChangedGapsFilter: null,
  onChangeCompareToRunConfiguration: null,
  onChangeExcludeZeroMembership: null,
  onChangePerspectiveToZipClass: null,
  onChangePerspectiveToZipCode: null,
  onChangeProjectStatus: null,
  onChangeRunConfiguration: null,
  onChangeSelectedMemberships: null,
  onChangeTablePage: null,
  onClickSpecialtyGroupOrZipCode: null,
  onEditTimeslicePublishComment: null,
  onFinishProcessingException: null,
  onLoadPage: null,
  onNcpProjectRunStatusChange: null,
  onProcessProviderChanges: null,
  onProcessSpecialtyExceptions: null,
  onPublishTimeSlice: null,
  onResetFilters: null,
  onRunProject: null,
  onSortTable: null,
  onSubmitEditProject: null,
  onSubmitSpecialtyException: null,
  onUpdateUnprocessedProjectionProviders: null,
  onUpdateUnprocessedProviders: null,
  onUpdateUnprocessedTerminations: null,
  state: {
    adequacyFilters: {
      active: true,
      all: true,
      approved: true,
      changedGaps: null,
      exceptions: false,
      excludeZeroMembership: false,
      gaps: false,
      opportunity: false,
      pending: true,
      specialtyType: null,
      viewBy: ProjectPerspective.ByZipCode,
    },
    attestationRateDays: AttestationRateDays.ThreeSixtyFive,
    availablePerspectives: [],
    calculationMethod: null,
    calculationMethodCompared: null,
    compareToRunConfiguration: null,
    dualMembershipRunConfiguration: null,
    error: null,
    filterOptions: {
      city: [],
      countiesAndStates: [],
      county: [],
      metOverall: [],
      metPassPercent: [],
      specialtyGroupCode: [],
      state: [],
      zipClass: [],
      zipCode: [],
    },
    filterSelections: initialFilterSelections,
    filterSelectionsTemp: initialFilterSelections,
    hasExpandableSpecialties: false,
    isCompareMode: false,
    isDrilledIn: false,
    isDualMembershipMode: false,
    loading: true,
    // the module currently never changes because summary relies on route instead of state
    // that should probably be refactored when more modules are added to zip ui, like pci
    module: "Adequacy",
    ncpProjectRunStatus: ProjectRunConfigurationStatus.Unknown,
    perspective: ProjectPerspective.ByZipCode,
    processProviderChanges: () => null,
    project: null,
    regionSetIds: [],
    regionSetSpecialtyGroupToPartsLookup: {},
    runConfiguration: null,
    runFrequency: TimeFrequency.All,
    selectedRows: [],
    setIsDualMembershipMode: () => null,
    setNcpProjectRunStatus: () => null,
    setProjectHasUnprocessedProjectionProviders: () => null,
    setProjectProjectionFlags: () => null,
    setShouldRefreshMarketProviders: () => null,
    setShouldRefreshNetNewProviders: () => null,
    setShouldRefreshNetworkProviders: () => null,
    setShouldRefreshProspectProviders: () => null,
    shouldRefreshMarketProviders: false,
    shouldRefreshNetNewProviders: false,
    shouldRefreshNetworkProviders: false,
    shouldRefreshProspectProviders: false,
    shouldShowSummariesTab: false,
    skip: 0,
    sortOption: {
      column: null,
      direction: SortDirections.None,
    },
    sortResetCount: 0,
    specialtyExceptionsMap: new Map<number, TimesliceExceptions>(),
    take: 50,
    templateMetadata: null,
    templateMetadataCompared: null,
    totalNumberOfRecords: 0,
    totalNumberOfRecordsSecondary: 0,
    trendChartData: [],
    urlSearchString: window.location.search.substring(1),
    zipClassDrillInDetails: [],
    zipDetails: [],
    zipDetailsCompared: [],
    zipExpansions: new Map<string, ZipCodeDisplayModel[] | ZipClassAggregateResultDisplayModel[]>(),
    zipExpansionsCompared: new Map<string, ZipCodeDisplayModel[] | ZipClassAggregateResultDisplayModel[]>(),
  }
}

const getNetworkAssessmentAnalysisType = (config: ProjectRunConfiguration): NetworkAssessmentAnalysisType => {
  if (config.isProjected) return NetworkAssessmentAnalysisType.ProjectedServiceArea;
  if (config.isProjectedTermed) return NetworkAssessmentAnalysisType.ProjectedTermedServiceArea;
  if (config.isTermed) return NetworkAssessmentAnalysisType.TermedServiceArea;
  return NetworkAssessmentAnalysisType.Unknown;
};

// jQuery rejects an aborted xhr like any other failure, and it reports status 0 for genuine network
// failures (connection refused, DNS, CORS) as well as for aborts — so the jqXHR alone can't tell the
// two apart. The signal we own can. Deliberate cancellations must not surface as a project load
// error, but a network failure has to keep going through the error dispatch so `loading` gets cleared.
export const isAbortRejection = (error: JQuery.jqXHR, signal: AbortSignal): boolean =>
  signal.aborted || error?.statusText === "abort";

export const ZipProjectContext = createContext(initialValues);

let lastLoadedProjectId: string | undefined;

export const ZipProjectContextProvider: FC<ZipContextInProps> = (props: ZipContextInProps) => {
  /**
   * Component Setup
   */
  const [state, dispatch] = useReducer(zipProjectReducer, initialValues.state);
  const { enabledFeatureFlags } = useContext(FeatureFlagContext);
  const history = useHistory();
  const match = useRouteMatch<{ projectId: string, module: ProjectModule }>();
  const projectService = useRef(new ProjectService()).current;
  const zipProjectService = useRef(new ZipProjectService()).current;
  const projectRunConfigurationService = useRef(new ProjectRunConfigurationService()).current;
  const templateService = useRef(new TemplateService()).current;
  const specialtyGroupExceptionsService = useRef(new ExceptionsService()).current;
  // The zip filter-options calls are the slowest requests on this page. If the user navigates away
  // mid-load the xhr otherwise stays queued in the browser forever, and a handful of abandoned loads
  // is enough to exhaust the per-host connection pool and wedge the tab.
  const filterOptionsAbortController = useRef(new AbortController());
  const urlShouldReplace = useRef(false);
  const ncpJobsExpected = useRef(0);
  const ncpJobsCompleted = useRef(0);
  const featureFlagCount = useRef(enabledFeatureFlags.size);

  /**
   * Handling side effects.
   * These are typically caused by changes to the URL that are caused by
   * actors outside the react app, like a user updating the address directly
   * or navigating through the history list.
   */

  // Cancel any in-flight filter-options request when the user leaves the project. Without this the
  // abandoned xhr stays queued in the browser indefinitely.
  useEffect(() => {
    const controller = filterOptionsAbortController.current;
    return () => controller.abort();
  }, []);

  // if the user arrived here from clicking a network assessment dashboard tile while
  // dashboard filters were applied, we'll need to convert some /county/x/specialty/y bits
  // to our search params
  useEffect(() => {
    const partsToExtract = ["county", "state", "specialty", "zip"];
    const { updatedPathname, extractedValues } = extractUrlParts(partsToExtract);
    // county projects use /specialty/ in the url, but the zip api uses specialtyGroupCode
    // that conversion has to be made somewhere, so right now it's here
    extractedValues.specialtyGroupCode = extractedValues.specialty;
    delete extractedValues.specialty;
    const updatedUrlSearchParams = parseQuerystring(history.location.search);
    Object.entries(extractedValues).forEach(([key, value]) => {
      if (!!value) {
        updatedUrlSearchParams[key] = value;
      }
    });
    replaceHistory(updatedPathname, querystringify(updatedUrlSearchParams));
  }, []);

  // if the pathname doesn't include any module, add it
  // this assumes there's nothing else in the pathname after the project id
  useEffect(() => {
    let newPathname = `${history.location.pathname}`;
    if (!match.params.module) {
      newPathname += `/${state.module.toLowerCase()}`;
      // replace the history item here so we don't have to click back twice to return to the dashboard
      replaceHistory(newPathname, history.location.search);
    }
  }, [history.location.pathname]);

  // update the url to stay in sync with state
useEffect(() => {
  if (!state.project?.projectId) {
      return;
  }
  if ((match.params.module as ZipProjectModuleRoutesEnum) === ZipProjectModuleRoutesEnum.ProviderHub) {
    urlShouldReplace.current = false;
    return;
  }
  const pathname = getUrlPathname(
    state.project.projectId,
    translateUrlModuleToProjectModule((match.params.module ?? state.module) as ZipProjectModuleRoutesEnum)
  );
  if (urlShouldReplace.current) { replaceHistory(pathname, state.urlSearchString); urlShouldReplace.current = false; }
  else { pushHistory(pathname, state.urlSearchString); }
}, [state.urlSearchString]);

  // if the search params are updated
  useEffect(() => {
    try {
      if (!state.project?.projectId || match.params.module !== "Adequacy".toLowerCase()) {
        return;
      }
      /**
       * This could have been triggered by either new filters being applied,
       * or by a user going back or forward in their history.
       * We need to check the filters to determine whether it's the second case,
       * and if it is, refresh the page data.
       */
      const urlSearch = new URLSearchParams(history.location.search);
      urlSearch.sort();
      const stateSearch = new URLSearchParams(getUrlSearchString(state.filterSelections, state.adequacyFilters, state.runConfiguration?.projectRunConfigurationId, state.compareToRunConfiguration?.projectRunConfigurationId));
      stateSearch.sort();
      const theyMatch = urlSearch.toString() === stateSearch.toString();

      // if the filters are different to state, it means they went through history,
      // instead of the URL being updated according to new filters
      if (!theyMatch) {
        dispatch({
          payload: { loading: true },
          type: ZipProjectActionsEnum.Loading,
        });
        const newPerspective = urlSearch.get("viewBy") as ProjectPerspective;

        getNewFilterOptions(newPerspective, state.project.projectId, state.runConfiguration, state.compareToRunConfiguration).then((newFilterOptions) => {
          // Don't need to update search params because that's why we're here already
          // Use initialValues.state.filterSelections so we don't include filters that were removed from the search params,
          // basically building a whole new set
          const { newFilterSelections } = syncFilterSelectionsToSearchParams(newFilterOptions, initialValues.state.filterSelections);
          const newAdequacyFilters = getAdequacyFiltersFromSearchParameters(parseQuerystring(history.location.search));
          Promise.all([
            getNewZipDetails(state.project, newPerspective, 0, undefined, newFilterSelections, undefined, undefined, undefined, newAdequacyFilters),
            getNewTrendChartData(newPerspective || state.perspective, state.project, getSearchParametersFromFilterSelections(newFilterSelections)),
            getFilteredZipCodeRegionSetIds(newPerspective, state.project.projectId, state.runConfiguration.projectRunConfigurationId, newFilterSelections)
          ]).then(([newZipDetails, newTrendChartData, newRegionSetIds]) => {
            const newIsDrilledIn = getIsDrilledIn(newZipDetails[0]?.data);
            const newRssgLookup = getRegionSetSpecialtyGroupIdLookup(newZipDetails[0]?.data, newZipDetails[1]?.data);
            const newZipExpansions = formatExpansions(newZipDetails[0]?.specialtyLevelResults);
            const newZipExpansionsCompared = formatExpansions(newZipDetails[1]?.specialtyLevelResults);
            const newSearchString = getUrlSearchString(newFilterSelections, newAdequacyFilters, state.runConfiguration?.projectRunConfigurationId, state.compareToRunConfiguration?.projectRunConfigurationId);
            const { topConfig, bottomConfig } = getTopAndBottomRunConfigurations(state.runConfiguration, state.compareToRunConfiguration, state.isCompareMode, state.isDualMembershipMode);
            const calculationMethodPromises = [getCalculationMethod(topConfig, newZipDetails[0]?.data), getCalculationMethod(bottomConfig, newZipDetails[1]?.data)];

            Promise.all(calculationMethodPromises).then(([newCalculationMethod, newCalculationMethodCompared]) => {
              urlShouldReplace.current = true;

              dispatch({
                payload: {
                  adequacyFilters: newAdequacyFilters,
                  calculationMethod: newCalculationMethod,
                  calculationMethodCompared: newCalculationMethodCompared,
                  filterOptions: newFilterOptions,
                  filterSelections: newFilterSelections,
                  filterSelectionsTemp: newFilterSelections,
                  hasExpandableSpecialties: newZipDetails[0]?.hasExpandableSpecialties || newZipDetails[1]?.hasExpandableSpecialties,
                  isDrilledIn: newIsDrilledIn,
                  loading: false,
                  module: match.params.module,
                  perspective: newPerspective || state.perspective,
                  regionSetIds: newRegionSetIds,
                  regionSetSpecialtyGroupToPartsLookup: newRssgLookup,
                  shouldRefreshMarketProviders: newIsDrilledIn,
                  shouldRefreshNetworkProviders: newIsDrilledIn,
                  shouldRefreshProspectProviders: newIsDrilledIn,
                  shouldShowSummariesTab: getShouldShowSummariesTab(state.runConfiguration, newFilterSelections),
                  totalNumberOfRecords: newZipDetails[0]?.totalNumberOfRecords,
                  totalNumberOfRecordsSecondary: newZipDetails[1]?.totalNumberOfRecords,
                  trendChartData: newTrendChartData,
                  urlSearchString: newSearchString,
                  zipDetails: newZipDetails[0]?.data,
                  zipDetailsCompared: newZipDetails[1]?.data,
                  zipExpansions: newZipExpansions,
                  zipExpansionsCompared: newZipExpansionsCompared,
                },
                type: ZipProjectActionsEnum.NavigateHistory,
              });
            });
          });
        }).catch((error) => {
          // This chain isn't awaited, so the enclosing try/catch can't see its rejections.
          if (isAbortRejection(error, filterOptionsAbortController.current.signal)) return;
          dispatch({
            payload: { error: error, loading: false },
            type: ZipProjectActionsEnum.Error
          });
        });
      }
    } catch (error) {
      dispatch({
        payload: { error: error, loading: false },
        type: ZipProjectActionsEnum.Error
      });
    }
  }, [history.location.search]);

  // if the feature flags load in late
  // Notes: There's a chance the feature flags API request returns during the initial async load project/change perspective functions,
  // in which case this useEffect is triggered a second time before the project is loaded and then never again (if that's the only dependency).
  // That's why the project model is also a dependency. But, we don't want this to run every time the project model updates,
  // so that's why the size of the feature flags set is also tracked and compared.
  useEffect(() => {
    if (state.project?.projectRunConfigurations?.length > 0 && enabledFeatureFlags.size !== featureFlagCount.current) {
      featureFlagCount.current = enabledFeatureFlags.size;
      if (enabledFeatureFlags.has(Features.ZIP_ANALYSIS_TRENDING) || enabledFeatureFlags.has(Features.ZIP_EXCEPTIONS)) {
        dispatch({
          payload: {
            loading: true,
          },
          type: ZipProjectActionsEnum.Loading,
        });
      }

      type PotentialPromises = typeof state.trendChartData | typeof state.specialtyExceptionsMap;
      const promises: Promise<PotentialPromises>[] = [];
      // since Promise.all just returns an array of responses without keys, we need to ensure a consistent order
      // so if the feature flag is off we make a Promise that auto-resolves and returns the existing value
      promises.push(enabledFeatureFlags.has(Features.ZIP_ANALYSIS_TRENDING) && state.trendChartData.length === 0
        ? getNewTrendChartData(state.perspective, state.project, getSearchParametersFromFilterSelections(state.filterSelections))
        : (async () => state.trendChartData)()
      );

      // If the feature flag was disabled, we would have stored null for this config's exceptions
      // otherwise it'd be an object, even if there aren't any
      promises.push(enabledFeatureFlags.has(Features.ZIP_EXCEPTIONS) && state.specialtyExceptionsMap.get(state.runConfiguration.projectRunConfigurationId) === null
        ? getNewSpecialtyExceptionsMap(state.project.projectId, state.runConfiguration.projectRunConfigurationId, state.compareToRunConfiguration?.projectRunConfigurationId || state.dualMembershipRunConfiguration?.projectRunConfigurationId, true)
        : (async () => state.specialtyExceptionsMap)()
      );

      Promise.all(promises).then((responses) => {
        const newTrendChartData = responses[0] as typeof state.trendChartData;
        const newSpecialtyExceptionsMap = responses[1] as typeof state.specialtyExceptionsMap;

        // Apply the feature-flag filter to project run configurations now that flags are resolved.
        // This covers the race where getProjectData completed before flags were available and
        // skipped the filter, leaving projected/termed configs unfiltered in state.
        const isTargetingEnabled = enabledFeatureFlags.has(Features.ZIP_CODE_ANALYSIS_TARGETING);
        const isTerminationEnabled = enabledFeatureFlags.has(Features.ZIP_CODE_ANALYSIS_TERMINATION);
        const updatedProject = state.project ? {
          ...state.project,
          projectRunConfigurations: state.project.projectRunConfigurations?.filter(prc =>
            (!prc.isProjected || isTargetingEnabled) &&
            (!prc.isTermed || isTerminationEnabled) &&
            (!prc.isProjectedTermed || (isTargetingEnabled && isTerminationEnabled))
          )
        } : state.project;

        dispatch({
          payload: {
            loading: false,
            project: updatedProject,
            specialtyExceptionsMap: newSpecialtyExceptionsMap,
            trendChartData: newTrendChartData,
          },
          type: ZipProjectActionsEnum.LoadedFeatureFlags
        });
      })
    }
  }, [enabledFeatureFlags, state.project]);

  /**
   * Helper methods.
   * These are simple methods that do one thing and are called from multiple other places in the file
   */
  const getProjectData = async (projectId: string) => {
    const project = await projectService.getById(parseInt(projectId, 10), true, false);

    // Only apply the feature-flag filter if the flags have already been loaded.
    // When enabledFeatureFlags is empty the flags haven't resolved yet; the useEffect
    // that fires when they do will apply the correct filter at that point.
    if (enabledFeatureFlags.size > 0) {
      const isTargetingEnabled = enabledFeatureFlags.has(Features.ZIP_CODE_ANALYSIS_TARGETING);
      const isTerminationEnabled = enabledFeatureFlags.has(Features.ZIP_CODE_ANALYSIS_TERMINATION);
      project.projectRunConfigurations = project.projectRunConfigurations?.filter(prc =>
        (!prc.isProjected || isTargetingEnabled) &&
        (!prc.isTermed || isTerminationEnabled) &&
        (!prc.isProjectedTermed || (isTargetingEnabled && isTerminationEnabled))
      );
    }

    return project;
  }

  const getIsDrilledIn = (data: ZipCodeDisplayModel[] | ZipClassAggregateResultDisplayModel[], perspective: ProjectPerspective = state.perspective) => {
    if (!data || data.length === 0)
      return false;

    switch (perspective) {
      case ProjectPerspective.ByZipClass:
        const { zipClass, specialtyGroupCode } = data[0];
        return data.every((d) => d.zipClass === zipClass && d.specialtyGroupCode === specialtyGroupCode);
      case ProjectPerspective.ByZipCode:
        const regionSetSpecialtyGroupId = (data[0] as ZipCodeDisplayModel)?.regionSetSpecialtyGroupId;
        return (data as ZipCodeDisplayModel[]).every((d) => d.regionSetSpecialtyGroupId === regionSetSpecialtyGroupId);
    }
  }

  const getProjectRunStatus = async (projectRunConfigurationId: number, statusType = ProjectRunStatusTypeEnum.ProjectRunStatus): Promise<ProjectRunStatusObject> => {
    const response = await projectRunConfigurationService.getProjectRunConfigurationStatus(projectRunConfigurationId, statusType);
    if (!response) {
      return {
        status: ProjectRunConfigurationStatus.Unknown,
        statusSeverity: StatusSeverity.Informational,
        statusText: "Unknown"
      }
    }

    return response;
  }

  // the regionSetSpecialtyGroupIdLookup is used to map rssg ids to related pieces,
  // so when someone clicks "create report" it's easy to get those pieces and pass them
  // to export data and then the api, and for specialty group exception keys
  const getRegionSetSpecialtyGroupIdLookup = (zipDetails: ZipCodeDisplayModel[] | ZipClassAggregateResultDisplayModel[], comparedDetails?: ZipCodeDisplayModel[] | ZipClassAggregateResultDisplayModel[]) => {
    const updateLookup = (details: ZipCodeDisplayModel[] | ZipClassAggregateResultDisplayModel[], newLookup: typeof state.regionSetSpecialtyGroupToPartsLookup) => {
      // right now, the zip class perspective doesn't use this or anything like it, so
      // this conditional essentially checks the perspective of the data and returns early if it's not zip code
      if ((details as ZipCodeDisplayModel[]).some((d) => !d.regionSetSpecialtyGroupId)) {
        return newLookup;
      }
      (details as ZipCodeDisplayModel[]).forEach((item) => {
        newLookup[item.regionSetSpecialtyGroupId.toString()] = {
          regionSetId: item.regionSetId,
          specialtyGroupCode: item.specialtyGroupCode.split(" - ")[0],
          zipCode: item.zipCode
        };
      })
      return newLookup;
    }

    let newRssgLookup = structuredClone(state.regionSetSpecialtyGroupToPartsLookup);
    newRssgLookup = updateLookup(zipDetails, newRssgLookup);
    if (!!comparedDetails) {
      newRssgLookup = updateLookup(comparedDetails, newRssgLookup);
    }
    return newRssgLookup;
  }

  const getZipClassAnalysisResults = async (
    projectData: Project,
    newSkip?: number,
    newSortOption?: ZipAnalysisSortOption,
    newFilterSelections?: ZipAnalysisSearchSelections,
    newRunConfiguration?: ProjectRunConfiguration,
  ) => {
    const query: ZipAnalysisQueryParameters = {
      excludeZeroMembership: false,
      limit: state.take,
      networkAssessmentAnalysisType: getNetworkAssessmentAnalysisType(newRunConfiguration),
      offset: newSkip === undefined ? state.skip : newSkip,
      searchParameters: getSearchParametersFromFilterSelections(newFilterSelections),
      sortOptions: !!newSortOption ? [newSortOption] : [initialValues.state.sortOption],
    };

    const newZipDetailsResponse = await zipProjectService.getZipClassResults(projectData.projectId, newRunConfiguration.projectRunConfigurationId, query);

    return newZipDetailsResponse;
  }

  const getZipCodeAnalysisResults = async (
    projectData: Project,
    newSkip?: number,
    newSortOption?: ZipAnalysisSortOption,
    newFilters?: ZipAnalysisSearchSelections,
    newProjectRunConfiguration?: ProjectRunConfiguration,
    excludeZeroMembership?: boolean
  ) => {
    const query: ZipAnalysisQueryParameters = {
      excludeZeroMembership: excludeZeroMembership ?? false,
      limit: state.take,
      networkAssessmentAnalysisType: getNetworkAssessmentAnalysisType(newProjectRunConfiguration),
      offset: newSkip === undefined ? state.skip : newSkip,
      searchParameters: getSearchParametersFromFilterSelections(newFilters || state.filterSelections),
      sortOptions: newSortOption ? [newSortOption] : [state.sortOption],
    };

    return await zipProjectService.getZipCodeResults(projectData.projectId, (newProjectRunConfiguration || state.runConfiguration).projectRunConfigurationId, query);
  }

  const getZipClassAnalysisCompareModeResults = async (
    projectData: Project,
    newSkip: number,
    newSortOption: ZipAnalysisSortOption,
    newFilterSelections: ZipAnalysisSearchSelections,
    newRunConfiguration: ProjectRunConfiguration,
    newCompareToRunConfiguration: ProjectRunConfiguration,
    changedGapsFilter: ChangedGapsEnum,
  ) => {
    const query: ComparingZipAnalysisQueryParameters = {
      changedGaps: changedGapsFilter,
      getGaps: false,
      parameters: {
        excludeZeroMembership: false,
        limit: state.take,
        offset: newSkip === undefined ? state.skip : newSkip,
        searchParameters: getSearchParametersFromFilterSelections(newFilterSelections),
        sortOptions: !!newSortOption ? [newSortOption] : [initialValues.state.sortOption],
      },
      projectRunConfigurationId1: newRunConfiguration.projectRunConfigurationId,
      projectRunConfigurationId2: newCompareToRunConfiguration.projectRunConfigurationId,
    };

    const newZipDetailsResponse = await zipProjectService.getZipClassCompareModeResults(projectData.projectId, query);

    return newZipDetailsResponse;
  }

  const getZipCodeAnalysisCompareModeResults = async (
    projectData: Project,
    newSkip: number,
    newSortOption: ZipAnalysisSortOption,
    newFilters: ZipAnalysisSearchSelections,
    newRunConfiguration: ProjectRunConfiguration,
    newCompareToRunConfiguration: ProjectRunConfiguration,
    changedGapsFilter: ChangedGapsEnum,
    excludeZeroMembership: boolean
  ) => {
    const query: ComparingZipAnalysisQueryParameters = {
      changedGaps: changedGapsFilter,
      getGaps: false,
      parameters: {
        excludeZeroMembership: excludeZeroMembership,
        limit: state.take,
        offset: newSkip === undefined ? state.skip : newSkip,
        searchParameters: getSearchParametersFromFilterSelections(newFilters || state.filterSelections),
        sortOptions: newSortOption ? [newSortOption] : [state.sortOption],
      },
      projectRunConfigurationId1: newRunConfiguration.projectRunConfigurationId,
      projectRunConfigurationId2: newCompareToRunConfiguration.projectRunConfigurationId,
    };

    const newZipDetailsResponse = await zipProjectService.getZipCodeCompareModeResults(projectData.projectId, query);

    return newZipDetailsResponse;
  }

  const getFilteredZipCodeRegionSetIds = async (perspective: ProjectPerspective, projectId: number, runConfigurationId: number, filterSelections: ZipAnalysisSearchSelections) => {
    if (perspective !== ProjectPerspective.ByZipCode) {
      return [];
    }
    const searchParameters = getSearchParametersFromFilterSelections(filterSelections);
    return await zipProjectService.getFilteredRegionSetIds(projectId, runConfigurationId, searchParameters);
  }

  const getNewZipDetails = async (
    projectData: Project,
    perspective?: ProjectPerspective,
    skip?: number,
    sortOption?: ZipAnalysisSortOption,
    filterSelections?: ZipAnalysisSearchSelections,
    runConfiguration?: ProjectRunConfiguration,
    compareToRunConfiguration?: ProjectRunConfiguration,
    exitCompareMode?: boolean,
    adequacyFilters?: AdequacyFilters,
  ) => {
    const newPerspective = perspective || state.perspective;
    // if the skip param is 0 it'd be falsey and default to the state value
    const newSkip = skip !== undefined ? skip : state.skip;
    const newSortOption = sortOption || state.sortOption;
    const newFilters = filterSelections || state.filterSelections;
    const newRunConfiguration = runConfiguration || state.runConfiguration;
    const newCompareToRunConfiguration = exitCompareMode ? undefined : compareToRunConfiguration || state.compareToRunConfiguration;
    const changedGaps = (adequacyFilters || state.adequacyFilters).changedGaps;
    const excludeZeroMembership = (adequacyFilters || state.adequacyFilters).excludeZeroMembership;

    if (!!newCompareToRunConfiguration) {
      switch (newPerspective) {
        case ProjectPerspective.ByZipClass:
          return await getZipClassAnalysisCompareModeResults(projectData, newSkip, newSortOption, newFilters, newRunConfiguration, newCompareToRunConfiguration, changedGaps);
        case ProjectPerspective.ByZipCode:
        default:
          return await getZipCodeAnalysisCompareModeResults(projectData, newSkip, newSortOption, newFilters, newRunConfiguration, newCompareToRunConfiguration, changedGaps, excludeZeroMembership);
      }
    }

    switch (newPerspective) {
      case ProjectPerspective.ByZipClass:
        return await getZipClassAnalysisResults(projectData, newSkip, newSortOption, newFilters, newRunConfiguration);
      case ProjectPerspective.ByZipCode:
      default:
        return await getZipCodeAnalysisResults(projectData, newSkip, newSortOption, newFilters, newRunConfiguration, excludeZeroMembership);
    }
  }

  const getNewTrendChartData = async (perspective: ProjectPerspective, project: Project, searchParameters: ZipAnalysisSearchParameters) => {
    if (!enabledFeatureFlags.has(Features.ZIP_ANALYSIS_TRENDING)) {
      return [];
    }
    try {
      switch (perspective) {
        case ProjectPerspective.ByZipClass:
          return enabledFeatureFlags.has(Features.ZIP_CODE_ANALYSIS_TARGETING) || enabledFeatureFlags.has(Features.ZIP_CODE_ANALYSIS_TERMINATION)
            ? enrichTrendChartWithDiplayNames(await zipProjectService.getZipClassTrendChartData(project.resource.resourceId, searchParameters))
            : await zipProjectService.getZipClassTrendChartData(project.resource.resourceId, searchParameters);
        case ProjectPerspective.ByZipCode:
          return enabledFeatureFlags.has(Features.ZIP_CODE_ANALYSIS_TARGETING) || enabledFeatureFlags.has(Features.ZIP_CODE_ANALYSIS_TERMINATION)
            ? enrichTrendChartWithDiplayNames(await zipProjectService.getZipCodeTrendChartData(project.resource.resourceId, searchParameters))
            : await zipProjectService.getZipCodeTrendChartData(project.resource.resourceId, searchParameters);
      }
    } catch (error) {
      // there probably aren't any run configurations available
      if (error.status === 404) {
        return [];
      }
      dispatch({
        payload: { error: error, loading: false },
        type: ZipProjectActionsEnum.Error,
      })
    }
  }

  const getTemplateMetadata = async (runConfiguration: ProjectRunConfiguration) => {
    // no reason to re-retrieve the template metadata if we already have it
    // undefined === undefined would be true
    if (runConfiguration?.adequacyTemplateCorrelationId === state.templateMetadata?.correlationId) {
      return state.templateMetadata;
    }
    if (!runConfiguration) {
      return undefined;
    }

    return await templateService.getTemplateById(runConfiguration.adequacyTemplateCollectionId, runConfiguration.adequacyTemplateId, false);
  }

  const getCalculationMethod = async (newRunConfiguration: ProjectRunConfiguration, newZipData: ZipCodeDisplayModel[] | ZipClassAggregateResultDisplayModel[], project: Project = state.project) => {
    if (!newZipData || newZipData.length === 0 || !newRunConfiguration) {
      return CalculationMethodEnum.Unknown;
    }

    if (newRunConfiguration.projectRunConfigurationId === state.runConfiguration?.projectRunConfigurationId && !!state.calculationMethod) {
      return state.calculationMethod;
    } else if (newRunConfiguration.projectRunConfigurationId === state.compareToRunConfiguration?.projectRunConfigurationId) {
      return state.calculationMethodCompared;
    }

    if ((enabledFeatureFlags.has(Features.ZIP_CODE_ANALYSIS_TARGETING) || enabledFeatureFlags.has(Features.ZIP_CODE_ANALYSIS_TERMINATION)) && (newRunConfiguration.isProjected || newRunConfiguration.isProjectedTermed || newRunConfiguration.isTermed)) {
      newRunConfiguration = project.projectRunConfigurations.find((prc) => prc.isLatestPublished && !prc.isProjected && !prc.isProjectedTermed && !prc.isTermed) ?? newRunConfiguration;
    }

    const specialtyGroupCode = newZipData[0]?.specialtyGroupCode;
    const calculationMethod = await templateService.getCalculationMethod(newRunConfiguration.projectRunConfigurationId, specialtyGroupCode);
    return calculationMethod;
  }

  const getNewSpecialtyExceptionsMap = async (newProjectId: number = state.project?.projectId, newRunConfigurationId: number = state.runConfiguration?.projectRunConfigurationId, secondRunConfigurationId?: number, forceRequest?: boolean) => {
    if (!newRunConfigurationId)
      return state.specialtyExceptionsMap;

    const updateMap = async (runConfigId: number, scopedNewMap: typeof state.specialtyExceptionsMap) => {
      if (!!scopedNewMap.has(runConfigId) && !forceRequest) {
        return scopedNewMap;
      } else {
        const newExceptions = enabledFeatureFlags.has(Features.ZIP_EXCEPTIONS) ? await specialtyGroupExceptionsService.getSpecialtyGroupExceptionsByRunConfigurationId(newProjectId, runConfigId) : null;
        let updatedTimesliceExceptions: TimesliceExceptions = null;
        if (newExceptions !== null) {
          updatedTimesliceExceptions = scopedNewMap.get(runConfigId) ?? {};
          newExceptions.forEach((exception) => {
            updatedTimesliceExceptions[getSpecialtyExceptionKey(exception)] = exception;
          })
        }
        scopedNewMap.set(runConfigId, updatedTimesliceExceptions);
        return scopedNewMap;
      }
    }

    let newMap = new Map(state.specialtyExceptionsMap);
    newMap = await updateMap(newRunConfigurationId, newMap);
    if (!!secondRunConfigurationId) {
      newMap = await updateMap(secondRunConfigurationId, newMap);
    }
    return newMap;
  }

  const getUrlPathname = (projectId?: number, module?: ProjectModule) => {
    const newProjectId = projectId || state.project.projectId;
    const newModule = module || state.module;
    return `/projects/${newProjectId}/${newModule.toLowerCase()}`;
  }

  const getFilterSelectionsFromSearchParameters = (searchParameters: { [key: string]: string }): ZipAnalysisSearchSelections => {
    // we only call this from keys that have DropdownItem values, so it's safe to use that type constraint
    const getSelectionFromOptions = (filterKey: keyof ZipAnalysisSearchParameters, selectionKey: string) => {
      return state.filterOptions[filterKey].find((item) => (item as DropdownItem<string>).key === selectionKey) as DropdownItem<string>;
    }

    return {
      city: !searchParameters.city ? initialValues.state.filterSelections.city : searchParameters.city,
      county: !searchParameters.county ? initialValues.state.filterSelections.county : getSelectionFromOptions("county", searchParameters.county),
      metOverall: !searchParameters.metOverall ? initialValues.state.filterSelections.metOverall : getSelectionFromOptions("metOverall", searchParameters.metOverall),
      metPassPercent: !searchParameters.metPassPercent ? initialValues.state.filterSelections.metPassPercent : getSelectionFromOptions("metPassPercent", searchParameters.metPassPercent),
      specialtyGroupCode: !searchParameters.specialtyGroupCode ? initialValues.state.filterSelections.specialtyGroupCode : getSelectionFromOptions("specialtyGroupCode", searchParameters.specialtyGroupCode),
      state: !searchParameters.state ? initialValues.state.filterSelections.state : getSelectionFromOptions("state", searchParameters.state),
      zipClass: !searchParameters.zipClass ? initialValues.state.filterSelections.zipClass : getSelectionFromOptions("zipClass", searchParameters.zipClass),
      zipCode: !searchParameters.zipCode ? initialValues.state.filterSelections.zipCode : searchParameters.zipCode,
    }
  };

  const getAdequacyFiltersFromSearchParameters = (searchParameters: { [key: string]: string }): AdequacyFilters => {
    return {
      active: searchParameters.active === "true" || initialValues.state.adequacyFilters.active,
      all: searchParameters.all === "true" || initialValues.state.adequacyFilters.all,
      approved: searchParameters.approved === "true" || initialValues.state.adequacyFilters.approved,
      changedGaps: searchParameters.changedGaps as ChangedGapsEnum || initialValues.state.adequacyFilters.changedGaps,
      exceptions: searchParameters.exceptions === "true" || initialValues.state.adequacyFilters.exceptions,
      excludeZeroMembership: searchParameters.excludeZeroMembership === "true" || initialValues.state.adequacyFilters.excludeZeroMembership,
      gaps: searchParameters.gaps === "true" || initialValues.state.adequacyFilters.gaps,
      opportunity: searchParameters.opportunity === "true" || initialValues.state.adequacyFilters.opportunity,
      pending: searchParameters.pending === "true" || initialValues.state.adequacyFilters.pending,
      specialtyType: searchParameters.specialtyType as SpecialtyTypeEnum || initialValues.state.adequacyFilters.specialtyType,
      viewBy: Object.values(ProjectPerspective).find((p) => p === searchParameters.viewBy),
    };
  }

  const extractUrlParts = (partKeys: string[]) => {
    let updatedPathname = history.location.pathname;
    const extractedValues: { [key: string]: string } = {};
    const urlParts = history.location.pathname.split("/");
    partKeys.forEach((key) => {
      const extractIndex = urlParts.findIndex((f) => f === key);
      if (extractIndex >= 0) {
        extractedValues[key] = urlParts[extractIndex + 1];
        urlParts.splice(extractIndex, 2);
      }
    })
    updatedPathname = urlParts.join("/");
    return { extractedValues, updatedPathname };
  }

  const getShouldShowSummariesTab = (runConfiguration: ProjectRunConfiguration, filterSelections: ZipAnalysisSearchSelections) => {
    return !!runConfiguration
      && runConfiguration.status === ProjectRunConfigurationStatus.Succeeded
      && !runConfiguration.showNetworkIntegrityDashboard
      && Object.values(filterSelections).every((value) => { return value === "" || value?.key === "all"; });
  }

  const pushHistory = (pathname: string, search: string) => {
    history.push(`${pathname}?${search}`);
  }

  const replaceHistory = (pathname: string, search: string) => {
    history.replace(`${pathname}?${search}`);
  }

  const getDefaultRunConfiguration = (projectData: Project) => {
    const candidateProjectRunConfigurations = !projectData.projectRunConfigurations ? [] : projectData.projectRunConfigurations.filter((prc: ProjectRunConfiguration) => (
      !prc.isProjected && !prc.isTermed && !prc.isProjectedTermed &&
      (prc.projectRunConfigurationType === ProjectRunConfigurationType.DualMembershipPrimary || prc.projectRunConfigurationType === ProjectRunConfigurationType.Standard)
    ))

    const orderedRunConfigurations = candidateProjectRunConfigurations.sort((rc1: ProjectRunConfiguration, rc2: ProjectRunConfiguration) => rc2.projectRunConfigurationId - rc1.projectRunConfigurationId)
    if (!!orderedRunConfigurations && props.user.roles.includes(Roles.PREVIEW)) {
      return (orderedRunConfigurations ?? [])[0]
    }

    const publishedRunConfigurations = orderedRunConfigurations.filter((rc: ProjectRunConfiguration) => !!rc.datePublished);
    //Where there is no published slice and user doesn't have PREVIEW role, then allow user to see the most recent timeslice.
    return publishedRunConfigurations.length > 0 ? publishedRunConfigurations[0] : orderedRunConfigurations[0];
  }

  const getLatestRunConfiguration = (projectData: Project) => {
    return projectData.projectRunConfigurations.filter((prc) => !prc.isProjected && !prc.isTermed && !prc.isProjectedTermed)[0];
  }

  const getZipCodeFilterOptions = async (projectId: number, runConfigurationId: number, isSucceeded: boolean, compareToRunConfigurationId?: number) => {
    return await zipProjectService.getZipCodeFilterOptions(projectId, runConfigurationId, isSucceeded, compareToRunConfigurationId, filterOptionsAbortController.current.signal);
  }

  const getZipClassFilterOptions = async (projectId: number, runConfigurationId: number, isSucceeded: boolean, compareToRunConfigurationId?: number) => {
    return await zipProjectService.getZipClassFilterOptions(projectId, runConfigurationId, isSucceeded, compareToRunConfigurationId, filterOptionsAbortController.current.signal);
  }

  const getNewFilterOptions = async (perspective: ProjectPerspective, projectId: number, runConfiguration: ProjectRunConfiguration, compareToRunConfiguration?: ProjectRunConfiguration) => {
    const runConfigurationId = runConfiguration?.projectRunConfigurationId;
    const isSucceeded = runConfiguration?.status === ProjectRunConfigurationStatus.Succeeded;
    switch (perspective) {
      case ProjectPerspective.ByZipClass:
        return await getZipClassFilterOptions(projectId, runConfigurationId, isSucceeded, compareToRunConfiguration?.projectRunConfigurationId);
      case ProjectPerspective.ByZipCode:
      default:
        return await getZipCodeFilterOptions(projectId, runConfigurationId, isSucceeded, compareToRunConfiguration?.projectRunConfigurationId);
    }
  }

  const setShouldRefreshNetworkProviders = (should: boolean = false) => {
    dispatch({
      payload: { shouldRefreshNetworkProviders: should, },
      type: ZipProjectActionsEnum.RenderedNetworkProviders,
    });
  };

  const setShouldRefreshProspectProviders = (should: boolean = false) => {
    dispatch({
      payload: { shouldRefreshProspectProviders: should, },
      type: ZipProjectActionsEnum.RenderedProspectProviders,
    });
  };

  const setShouldRefreshMarketProviders = (should: boolean = false) => {
    dispatch({
      payload: { shouldRefreshMarketProviders: should, },
      type: ZipProjectActionsEnum.RenderedMarketProviders,
    });
  };

  const onUpdateUnprocessedProjectionProviders = (hasUnprocessed: boolean = false, projectionType?: ProjectionProviderType) => {
    // A Target action must not mark exclusions as unprocessed, and vice versa, so only the affected
    // category moves. Callers that can't tell the two apart omit projectionType and move both.
    const affectsTargets = projectionType !== ProjectionProviderType.ExcludeImpact;
    const affectsExcludes = projectionType !== ProjectionProviderType.Target;
    const hasUnprocessedTargetProviders = affectsTargets ? hasUnprocessed : (state.project?.hasUnprocessedTargetProviders ?? false);
    const hasUnprocessedExcludedProviders = affectsExcludes ? hasUnprocessed : (state.project?.hasUnprocessedExcludedProviders ?? false);

    dispatch({
      payload: {
        project: {
          ...state.project,
          hasUnprocessedExcludedProviders,
          hasUnprocessedProjectionProviders: hasUnprocessedTargetProviders || hasUnprocessedExcludedProviders,
          hasUnprocessedTargetProviders,
        },
      },
      type: ZipProjectActionsEnum.UpdatedUnprocessedProviders,
    });
  };

  const onUpdateUnprocessedTerminations = (hasUnprocessed: boolean = false) => {
    dispatch({
      payload: {
        project: {
          ...state.project,
          hasUnprocessedProviderTerminations: hasUnprocessed,
        },
      },
      type: ZipProjectActionsEnum.UpdatedUnprocessedProviders,
    });
  };

  const onUpdateUnprocessedProviders = (hasUnprocessedTerminations: boolean, hasUnprocessedTargets: boolean, hasUnprocessedExcludes: boolean = false) => {
    dispatch({
      payload: {
        project: {
          ...state.project,
          hasUnprocessedExcludedProviders: hasUnprocessedExcludes,
          hasUnprocessedProjectionProviders: hasUnprocessedTargets || hasUnprocessedExcludes,
          hasUnprocessedProviderTerminations: hasUnprocessedTerminations,
          hasUnprocessedTargetProviders: hasUnprocessedTargets,
        },
      },
      type: ZipProjectActionsEnum.UpdatedUnprocessedProviders,
    });
  };

  const syncFilterSelectionsToSearchParams = (filterOptions: ZipAnalysisSearchOptions, filterSelections?: ZipAnalysisSearchSelections) => {
    const newUrlSearchParams = parseQuerystring(history.location.search);
    const newFilterSelections = structuredClone(filterSelections || state.filterSelections);
    // we take the search params - regardless of whether they're new - and generate
    // the dropdown filter selections based on those
    // This happens whenever the search params change to keep everything in sync
    const badKeys: string[] = [];
    Object.entries(newUrlSearchParams).forEach(([key, value]) => {
      const matchingFilterOption =
        filterOptions[key as keyof ZipAnalysisSearchOptions]?.find((option) => {
          return (option as DropdownItem<string>)?.key === value || option === value;
        });
      if (!!matchingFilterOption) {
        newFilterSelections[key as keyof ZipAnalysisSearchSelections] = matchingFilterOption as DropdownItem<string> & string;
      } else if (key === "zipCode" || key === "city") {
        newFilterSelections[key as keyof ZipAnalysisSearchSelections] = value as DropdownItem<string> & string;
      } else if (!Object.keys(state.adequacyFilters).includes(key)) {
        newFilterSelections[key as keyof ZipAnalysisSearchSelections] = initialValues.state.filterSelections[key as keyof ZipAnalysisSearchSelections] as DropdownItem<string> & string;
        badKeys.push(key);
      }
    });

    // and any query parameters that didn't match the filters get removed
    badKeys.forEach((key) => {
      delete newUrlSearchParams[key];
    })

    return { newFilterSelections, newUrlSearchParams };
  }

  const formatExpansions = (specialtyLevelResults: SpecialtyLevelResults) => {
    const newMap = new Map<string, ZipCodeDisplayModel[] | ZipClassAggregateResultDisplayModel[]>();
    if (!specialtyLevelResults) {
      return newMap;
    }
    Object.keys(specialtyLevelResults).forEach((key) => {
      newMap.set(key, specialtyLevelResults[key]);
    });
    return newMap;
  }

  const enrichTrendChartWithDiplayNames = (data: ZipClassTrendChartItem[] | ZipCodeTrendChartItem[]) => {
    data.forEach((trendChartItem) => {
      if (trendChartItem.itemType === TrendChartItemType.Projected)
        trendChartItem.displayName = `Projected`;
      else if (trendChartItem.itemType === TrendChartItemType.ProjectedTermed)
        trendChartItem.displayName = `Projected-Termed`;
      else if (trendChartItem.itemType === TrendChartItemType.Termed)
        trendChartItem.displayName = `Termed`;
      else
        trendChartItem.displayName = trendChartItem.dateCreatedOrProjected;
    })

    return data;
  }

  /**
   * Event handlers
   * These are called from components (typically as responses to user actions)
   * and do some middleware work before feeding the action into the reducer
   */

  const onRunProject = async (projectId: number) => {
    try {
      if (!!state.project && !state.project.isRunning) {
        await zipProjectService.runZipProject(projectId);
        const newFilterSelections = initialValues.state.filterSelections;
        const newAdequacyFilters = initialValues.state.adequacyFilters;
        const newUrlSearchString = getUrlSearchString(newFilterSelections, newAdequacyFilters, state.runConfiguration?.projectRunConfigurationId, state.compareToRunConfiguration?.projectRunConfigurationId);

        dispatch({
          payload: {
            calculationMethod: initialValues.state.calculationMethod,
            calculationMethodCompared: initialValues.state.calculationMethodCompared,
            filterSelections: initialValues.state.filterSelections,
            filterSelectionsTemp: initialValues.state.filterSelections,
            hasExpandableSpecialties: initialValues.state.hasExpandableSpecialties,
            isDrilledIn: false,
            project: {
              ...state.project,
              isEditRestricted: true,
              isReadOnly: true,
              isRunning: true,
            },
            regionSetIds: initialValues.state.regionSetIds,
            regionSetSpecialtyGroupToPartsLookup: {},
            selectedRows: [],
            skip: 0,
            sortOption: {
              column: null,
              direction: SortDirections.None,
            },
            totalNumberOfRecords: 0,
            totalNumberOfRecordsSecondary: 0,
            trendChartData: initialValues.state.trendChartData,
            urlSearchString: newUrlSearchString,
            zipDetails: [],
            zipDetailsCompared: [],
            zipExpansions: initialValues.state.zipExpansions,
            zipExpansionsCompared: initialValues.state.zipExpansionsCompared,
          },
          type: ZipProjectActionsEnum.StartRunProject,
        });
      }
    } catch (error) {
      dispatch({
        payload: { error: error, loading: false },
        type: ZipProjectActionsEnum.Error
      });
    }
  }

  const onSubmitEditProject = async (projectId: number) => {
    try {
      dispatch({
        payload: { loading: true },
        type: ZipProjectActionsEnum.Loading,
      });

      const projectData = await getProjectData(projectId.toString());
      // we need to request updated project data for the project change reasons and possible column changes
      const newZipDetailsResponse = await getNewZipDetails(projectData);
      const newRegionSetSpecialtyGroupToPartsLookup = getRegionSetSpecialtyGroupIdLookup(newZipDetailsResponse[0]?.data, newZipDetailsResponse[1]?.data);
      const newRunConfiguration = getDefaultRunConfiguration(projectData);
      const newTemplateMetadata = await getTemplateMetadata(newRunConfiguration);
      const newZipExpansions = formatExpansions(newZipDetailsResponse[0]?.specialtyLevelResults);
      const newZipExpansionsCompared = formatExpansions(newZipDetailsResponse[1]?.specialtyLevelResults);
      const { topConfig, bottomConfig } = getTopAndBottomRunConfigurations(newRunConfiguration, state.compareToRunConfiguration, state.isCompareMode, state.isDualMembershipMode);
      const newCalculationMethod = await getCalculationMethod(topConfig, newZipDetailsResponse[0]?.data);
      const newCalculationMethodCompared = await getCalculationMethod(bottomConfig, newZipDetailsResponse[1]?.data);
      const newFilterOptions = await getNewFilterOptions(state.perspective, projectData.projectId, newRunConfiguration, state.compareToRunConfiguration);
      const newFilterSelections = syncFilterSelectionsToSearchParams(newFilterOptions, state.filterSelections).newFilterSelections;
      const newIsDrilledIn = getIsDrilledIn(newZipDetailsResponse[0]?.data, state.perspective);
      const newRegionSetIds = await getFilteredZipCodeRegionSetIds(state.perspective, projectData.projectId, newRunConfiguration.projectRunConfigurationId, newFilterSelections);
      const newTrendChartData = await getNewTrendChartData(state.perspective, projectData, getSearchParametersFromFilterSelections(state.filterSelections));
      const newSpecialtyExceptionsMap = await getNewSpecialtyExceptionsMap(projectData.projectId, newRunConfiguration.projectRunConfigurationId, state.compareToRunConfiguration?.projectRunConfigurationId || state.dualMembershipRunConfiguration?.projectRunConfigurationId);

      dispatch({
        payload: {
          calculationMethod: newCalculationMethod,
          calculationMethodCompared: newCalculationMethodCompared,
          filterOptions: newFilterOptions,
          filterSelections: newFilterSelections,
          filterSelectionsTemp: newFilterSelections,
          hasExpandableSpecialties: newZipDetailsResponse[0]?.hasExpandableSpecialties || newZipDetailsResponse[1]?.hasExpandableSpecialties,
          isDrilledIn: newIsDrilledIn,
          loading: false,
          project: projectData,
          regionSetIds: newRegionSetIds,
          regionSetSpecialtyGroupToPartsLookup: newRegionSetSpecialtyGroupToPartsLookup,
          runConfiguration: newRunConfiguration,
          specialtyExceptionsMap: newSpecialtyExceptionsMap,
          templateMetadata: newTemplateMetadata,
          totalNumberOfRecords: newZipDetailsResponse[0]?.totalNumberOfRecords,
          totalNumberOfRecordsSecondary: newZipDetailsResponse[1]?.totalNumberOfRecords,
          trendChartData: newTrendChartData,
          zipDetails: newZipDetailsResponse[0]?.data,
          zipDetailsCompared: newZipDetailsResponse[1]?.data,
          zipExpansions: newZipExpansions,
          zipExpansionsCompared: newZipExpansionsCompared,
        },
        type: ZipProjectActionsEnum.EditProject,
      });
    } catch (error) {
      dispatch({
        payload: { error: error, loading: false },
        type: ZipProjectActionsEnum.Error
      });
    }
  }

  const onChangeChangedGapsFilter = async (newChangedGapsFilter: ChangedGapsEnum) => {
    try {
      dispatch({
        payload: { loading: true },
        type: ZipProjectActionsEnum.Loading,
      });

      const newSkip = 0;
      const newSort = initialValues.state.sortOption;
      const newAdequacyFilters = structuredClone(state.adequacyFilters);
      newAdequacyFilters.changedGaps = newChangedGapsFilter;
      newAdequacyFilters.excludeZeroMembership = false;

      const newZipDetailsResponse = await getNewZipDetails(state.project, undefined, newSkip, newSort, undefined, undefined, undefined, false, newAdequacyFilters);
      const newRegionSetSpecialtyGroupToPartsLookup = getRegionSetSpecialtyGroupIdLookup(newZipDetailsResponse[0]?.data, newZipDetailsResponse[1]?.data);
      const newSearchParameterString = getUrlSearchString(state.filterSelections, newAdequacyFilters, state.runConfiguration?.projectRunConfigurationId, state.compareToRunConfiguration?.projectRunConfigurationId);
      const newZipExpansions = formatExpansions(newZipDetailsResponse[0]?.specialtyLevelResults);
      const newZipExpansionsCompared = formatExpansions(newZipDetailsResponse[1]?.specialtyLevelResults);
      const { topConfig, bottomConfig } = getTopAndBottomRunConfigurations(state.runConfiguration, state.compareToRunConfiguration, state.isCompareMode, state.isDualMembershipMode);
      const newCalculationMethod = await getCalculationMethod(topConfig, newZipDetailsResponse[0]?.data);
      const newCalculationMethodCompared = await getCalculationMethod(bottomConfig, newZipDetailsResponse[1]?.data);

      dispatch({
        payload: {
          adequacyFilters: newAdequacyFilters,
          calculationMethod: newCalculationMethod,
          calculationMethodCompared: newCalculationMethodCompared,
          hasExpandableSpecialties: newZipDetailsResponse[0]?.hasExpandableSpecialties || newZipDetailsResponse[1]?.hasExpandableSpecialties,
          isDrilledIn: getIsDrilledIn(newZipDetailsResponse[0]?.data || newZipDetailsResponse[1]?.data),
          loading: false,
          regionSetIds: initialValues.state.regionSetIds,
          regionSetSpecialtyGroupToPartsLookup: newRegionSetSpecialtyGroupToPartsLookup,
          selectedRows: initialValues.state.selectedRows,
          totalNumberOfRecords: newZipDetailsResponse[0]?.totalNumberOfRecords,
          totalNumberOfRecordsSecondary: newZipDetailsResponse[1]?.totalNumberOfRecords,
          trendChartData: state.trendChartData,
          urlSearchString: newSearchParameterString,
          zipDetails: newZipDetailsResponse[0]?.data,
          zipDetailsCompared: newZipDetailsResponse[1]?.data,
          zipExpansions: newZipExpansions,
          zipExpansionsCompared: newZipExpansionsCompared,
        },
        type: ZipProjectActionsEnum.ChangeChangedGapsFilter,
      })
    } catch (error) {
      dispatch({
        payload: { error: error, loading: false },
        type: ZipProjectActionsEnum.Error
      });
    }
  }

  const onChangeExcludeZeroMembership = async (excludeZeroMembership: boolean) => {
    try {
      dispatch({
        payload: { loading: true },
        type: ZipProjectActionsEnum.Loading,
      });

      const newSkip = 0;
      const newSort = initialValues.state.sortOption;
      const newAdequacyFilters = structuredClone(state.adequacyFilters);
      newAdequacyFilters.excludeZeroMembership = excludeZeroMembership;

      const newZipDetailsResponse = await getNewZipDetails(state.project, ProjectPerspective.ByZipCode, newSkip, newSort, state.filterSelections, state.runConfiguration, state.compareToRunConfiguration, false, newAdequacyFilters);
      const newRegionSetSpecialtyGroupToPartsLookup = getRegionSetSpecialtyGroupIdLookup(newZipDetailsResponse[0]?.data, newZipDetailsResponse[1]?.data);
      const newSearchParameterString = getUrlSearchString(state.filterSelections, newAdequacyFilters, state.runConfiguration?.projectRunConfigurationId, state.compareToRunConfiguration?.projectRunConfigurationId);
      const newZipExpansions = formatExpansions(newZipDetailsResponse[0]?.specialtyLevelResults);
      const newZipExpansionsCompared = formatExpansions(newZipDetailsResponse[1]?.specialtyLevelResults);
      const { topConfig, bottomConfig } = getTopAndBottomRunConfigurations(state.runConfiguration, state.compareToRunConfiguration, state.isCompareMode, state.isDualMembershipMode);
      const newCalculationMethod = await getCalculationMethod(topConfig, newZipDetailsResponse[0]?.data);
      const newCalculationMethodCompared = await getCalculationMethod(bottomConfig, newZipDetailsResponse[1]?.data);

      dispatch({
        payload: {
          adequacyFilters: newAdequacyFilters,
          calculationMethod: newCalculationMethod,
          calculationMethodCompared: newCalculationMethodCompared,
          hasExpandableSpecialties: newZipDetailsResponse[0]?.hasExpandableSpecialties || newZipDetailsResponse[1]?.hasExpandableSpecialties,
          isDrilledIn: getIsDrilledIn(newZipDetailsResponse[0]?.data || newZipDetailsResponse[1]?.data),
          loading: false,
          regionSetSpecialtyGroupToPartsLookup: newRegionSetSpecialtyGroupToPartsLookup,
          selectedRows: initialValues.state.selectedRows,
          totalNumberOfRecords: newZipDetailsResponse[0]?.totalNumberOfRecords,
          totalNumberOfRecordsSecondary: newZipDetailsResponse[1]?.totalNumberOfRecords,
          trendChartData: state.trendChartData,
          urlSearchString: newSearchParameterString,
          zipDetails: newZipDetailsResponse[0]?.data,
          zipDetailsCompared: newZipDetailsResponse[1]?.data,
          zipExpansions: newZipExpansions,
          zipExpansionsCompared: newZipExpansionsCompared,
        },
        type: ZipProjectActionsEnum.ChangeExcludeZeroMembership,
      })
    } catch (error) {
      dispatch({
        payload: { error: error, loading: false },
        type: ZipProjectActionsEnum.Error
      });
    }
  }

  /** Note on compare mode:
   * The runConfiguration and compareToRunConfiguration in state reflect the selections in the user's dropdowns and trend chart.
   * That is not necessarily the order in which the data is displayed.
   * The API automatically returns the older configuration's data set as item 0, and the newer configuration's data set as item 1.
   * That means, depending on the user's selections, either object in state could be item 0 or item 1. If the user changes the compareToRunConfiguration
   * from a run newer than the runConfiguration to one older than the runConfiguration, all of our zipDetails, zipDetailsCompared, zipExpansions, etc. get flipped,
   * so we can't assume anything is staying the same.
   */
  const onChangeCompareToRunConfiguration = async (newCompareToRunConfigurationId: number) => {
    try {
      dispatch({
        payload: { loading: true },
        type: ZipProjectActionsEnum.Loading
      });

      const newSkip = 0;
      const newSort = initialValues.state.sortOption;

      const newAdequacyFilters = { ...state.adequacyFilters, excludeZeroMembership: false };
      const newCompareToRunConfiguration = state.project.projectRunConfigurations.find((prc) => prc.projectRunConfigurationId === newCompareToRunConfigurationId);

      if (!!newCompareToRunConfiguration) {
        const { topConfig, bottomConfig } = getTopAndBottomRunConfigurations(state.runConfiguration, newCompareToRunConfiguration, true, false);

        const newFilterOptions = await getNewFilterOptions(state.perspective, state.project.projectId, state.runConfiguration, newCompareToRunConfiguration);
        // city and zipCode are just strings that don't need a matching key
        // the rest are DropdownItem<T>s
        const newFilters: ZipAnalysisSearchSelections = {
          city: state.filterSelections.city,
          county: newFilterOptions.county.find((s) => s.key === state.filterSelections.county.key) ? state.filterSelections.county : initialValues.state.filterSelections.county,
          metOverall: newFilterOptions.metOverall.find((s) => s.key === state.filterSelections.metOverall.key) ? state.filterSelections.metOverall : initialValues.state.filterSelections.metOverall,
          metPassPercent: newFilterOptions.metPassPercent.find((s) => s.key === state.filterSelections.metPassPercent.key) ? state.filterSelections.metPassPercent : initialValues.state.filterSelections.metPassPercent,
          specialtyGroupCode: newFilterOptions.specialtyGroupCode.find((s) => s.key === state.filterSelections.specialtyGroupCode.key) ? state.filterSelections.specialtyGroupCode : initialValues.state.filterSelections.specialtyGroupCode,
          state: newFilterOptions.state.find((s) => s.key === state.filterSelections.state.key) ? state.filterSelections.state : initialValues.state.filterSelections.state,
          zipClass: newFilterOptions.zipClass.find((s) => s.key === state.filterSelections.zipClass.key) ? state.filterSelections.zipClass : initialValues.state.filterSelections.zipClass,
          zipCode: state.filterSelections.zipCode,
        };
        let newTrendChartData = state.trendChartData;
        if (haveFilterSelectionsChanged(state.filterSelections, newFilters)) {
          newTrendChartData = await getNewTrendChartData(state.perspective, state.project, getSearchParametersFromFilterSelections(newFilters));
        }
        const newZipDetailsResponse = await getNewZipDetails(state.project, undefined, newSkip, newSort, newFilters, state.runConfiguration, newCompareToRunConfiguration, false, newAdequacyFilters);
        const newRegionSetSpecialtyGroupToPartsLookup = getRegionSetSpecialtyGroupIdLookup(newZipDetailsResponse[0]?.data, newZipDetailsResponse[1]?.data);
        const newSearchParameterString = getUrlSearchString(newFilters, state.adequacyFilters, state.runConfiguration?.projectRunConfigurationId, newCompareToRunConfiguration.projectRunConfigurationId);
        const newTemplateMetadataCompared = await getTemplateMetadata(newCompareToRunConfiguration);

        const newCalculationMethod = await getCalculationMethod(topConfig, newZipDetailsResponse[0]?.data);
        const newCalculationMethodCompared = await getCalculationMethod(bottomConfig, newZipDetailsResponse[1]?.data);
        const newZipExpansions = formatExpansions(newZipDetailsResponse[0]?.specialtyLevelResults);
        const newZipExpansionsCompared = formatExpansions(newZipDetailsResponse[1]?.specialtyLevelResults);

        const newSpecialtyExceptionsMap = await getNewSpecialtyExceptionsMap(state.project.projectId, state.runConfiguration.projectRunConfigurationId, newCompareToRunConfiguration.projectRunConfigurationId);
        const newIsDrilledIn = getIsDrilledIn(newZipDetailsResponse[0]?.data || newZipDetailsResponse[1]?.data);

        dispatch({
          payload: {
            adequacyFilters: newAdequacyFilters,
            calculationMethod: newCalculationMethod,
            calculationMethodCompared: newCalculationMethodCompared,
            compareToRunConfiguration: newCompareToRunConfiguration,
            filterOptions: newFilterOptions,
            filterSelections: newFilters,
            filterSelectionsTemp: newFilters,
            hasExpandableSpecialties: newZipDetailsResponse[0]?.hasExpandableSpecialties || newZipDetailsResponse[1]?.hasExpandableSpecialties,
            isCompareMode: true,
            isDrilledIn: newIsDrilledIn,
            loading: false,
            regionSetIds: initialValues.state.regionSetIds,
            regionSetSpecialtyGroupToPartsLookup: newRegionSetSpecialtyGroupToPartsLookup,
            selectedRows: initialValues.state.selectedRows,
            shouldRefreshMarketProviders: newIsDrilledIn,
            shouldRefreshNetNewProviders: newIsDrilledIn,
            shouldRefreshNetworkProviders: newIsDrilledIn,
            shouldRefreshProspectProviders: newIsDrilledIn,
            shouldShowSummariesTab: getShouldShowSummariesTab(newCompareToRunConfiguration, newFilters),
            skip: newSkip,
            sortOption: newSort,
            specialtyExceptionsMap: newSpecialtyExceptionsMap,
            templateMetadataCompared: newTemplateMetadataCompared,
            totalNumberOfRecords: newZipDetailsResponse[0]?.totalNumberOfRecords,
            totalNumberOfRecordsSecondary: newZipDetailsResponse[1]?.totalNumberOfRecords,
            trendChartData: newTrendChartData,
            urlSearchString: newSearchParameterString,
            zipDetails: newZipDetailsResponse[0]?.data,
            zipDetailsCompared: newZipDetailsResponse[1]?.data,
            zipExpansions: newZipExpansions,
            zipExpansionsCompared: newZipExpansionsCompared,
          },
          type: ZipProjectActionsEnum.ChangeCompareToProjectRunConfiguration
        });
      } else {
        // revert to non-compare mode
        const newFilterOptions = await getNewFilterOptions(state.perspective, state.project.projectId, state.runConfiguration);
        // reset changed gaps filter when leaving compare mode
        newAdequacyFilters.changedGaps = null;
        // city and zipCode are just strings that don't need a matching key
        // the rest are DropdownItem<T>s
        const newFilters: ZipAnalysisSearchSelections = {
          city: state.filterSelections.city,
          county: newFilterOptions.county.find((s) => s.key === state.filterSelections.county.key) ? state.filterSelections.county : initialValues.state.filterSelections.county,
          metOverall: newFilterOptions.metOverall.find((s) => s.key === state.filterSelections.metOverall.key) ? state.filterSelections.metOverall : initialValues.state.filterSelections.metOverall,
          metPassPercent: newFilterOptions.metPassPercent.find((s) => s.key === state.filterSelections.metPassPercent.key) ? state.filterSelections.metPassPercent : initialValues.state.filterSelections.metPassPercent,
          specialtyGroupCode: newFilterOptions.specialtyGroupCode.find((s) => s.key === state.filterSelections.specialtyGroupCode.key) ? state.filterSelections.specialtyGroupCode : initialValues.state.filterSelections.specialtyGroupCode,
          state: newFilterOptions.state.find((s) => s.key === state.filterSelections.state.key) ? state.filterSelections.state : initialValues.state.filterSelections.state,
          zipClass: newFilterOptions.zipClass.find((s) => s.key === state.filterSelections.zipClass.key) ? state.filterSelections.zipClass : initialValues.state.filterSelections.zipClass,
          zipCode: state.filterSelections.zipCode,
        };
        let newTrendChartData = state.trendChartData;
        if (haveFilterSelectionsChanged(state.filterSelections, newFilters)) {
          newTrendChartData = await getNewTrendChartData(state.perspective, state.project, getSearchParametersFromFilterSelections(newFilters));
        }
        const newZipDetailsResponse = await getNewZipDetails(state.project, undefined, newSkip, newSort, newFilters, state.runConfiguration, undefined, true, newAdequacyFilters);
        const newSearchParameterString = getUrlSearchString(newFilters, state.adequacyFilters, state.runConfiguration?.projectRunConfigurationId, undefined);
        const newCalculationMethod = await getCalculationMethod(state.runConfiguration, newZipDetailsResponse[0]?.data);
        const newZipExpansions = formatExpansions(newZipDetailsResponse[0]?.specialtyLevelResults);
        const newIsDrilledIn = getIsDrilledIn(newZipDetailsResponse[0]?.data);

        dispatch({
          payload: {
            adequacyFilters: newAdequacyFilters,
            calculationMethod: newCalculationMethod,
            calculationMethodCompared: initialValues.state.calculationMethodCompared,
            compareToRunConfiguration: initialValues.state.compareToRunConfiguration,
            filterOptions: newFilterOptions,
            filterSelections: newFilters,
            filterSelectionsTemp: newFilters,
            hasExpandableSpecialties: newZipDetailsResponse[0]?.hasExpandableSpecialties,
            isCompareMode: false,
            isDrilledIn: newIsDrilledIn,
            loading: false,
            regionSetIds: initialValues.state.regionSetIds,
            regionSetSpecialtyGroupToPartsLookup: state.regionSetSpecialtyGroupToPartsLookup,
            selectedRows: initialValues.state.selectedRows,
            shouldRefreshMarketProviders: newIsDrilledIn,
            shouldRefreshNetNewProviders: newIsDrilledIn,
            shouldRefreshNetworkProviders: newIsDrilledIn,
            shouldRefreshProspectProviders: newIsDrilledIn,
            shouldShowSummariesTab: getShouldShowSummariesTab(newCompareToRunConfiguration, newFilters),
            skip: newSkip,
            sortOption: newSort,
            specialtyExceptionsMap: state.specialtyExceptionsMap,
            templateMetadataCompared: initialValues.state.templateMetadataCompared,
            totalNumberOfRecords: newZipDetailsResponse[0]?.totalNumberOfRecords,
            totalNumberOfRecordsSecondary: newZipDetailsResponse[1]?.totalNumberOfRecords,
            trendChartData: newTrendChartData,
            urlSearchString: newSearchParameterString,
            zipDetails: newZipDetailsResponse[0]?.data,
            zipDetailsCompared: [],
            zipExpansions: newZipExpansions,
            zipExpansionsCompared: initialValues.state.zipExpansionsCompared,
          },
          type: ZipProjectActionsEnum.ChangeCompareToProjectRunConfiguration
        });
      }
    } catch (error) {
      dispatch({
        payload: { error: error, loading: false },
        type: ZipProjectActionsEnum.Error
      });
    }
  }

  const onChangePerspectiveToZipClass = async (project?: Project, runConfiguration?: ProjectRunConfiguration, compareToRunConfiguration?: ProjectRunConfiguration) => {
    try {
      dispatch({
        payload: { loading: true },
        type: ZipProjectActionsEnum.Loading,
      });
      const newPerspective = ProjectPerspective.ByZipClass;
      const newProject = project || state.project;
      const newRunConfiguration = runConfiguration || state.runConfiguration;
      // compareToRunConfiguration is only supplied on initial load (e.g. restored from the URL after
      // "Return to Project"); otherwise fall back to whatever compare selection is already in state.
      const newCompareToRunConfiguration = compareToRunConfiguration ?? state.compareToRunConfiguration;
      const newIsCompareMode = !!newCompareToRunConfiguration;
      const { topConfig, bottomConfig } = getTopAndBottomRunConfigurations(newRunConfiguration, newCompareToRunConfiguration, newIsCompareMode, state.isDualMembershipMode);

      const isInitialLoad = !!project && !!runConfiguration;
      const newDualMembershipRunConfiguration = newProject.projectRunConfigurations.find((rc) => rc.projectRunConfigurationId === newRunConfiguration.companionProjectRunConfigurationId);

      const newFilterOptions = await getNewFilterOptions(newPerspective, newProject.projectId, newRunConfiguration, newCompareToRunConfiguration);
      const searchParameters = parseQuerystring(history.location.search);
      const existingFilterSelections = getFilterSelectionsFromSearchParameters(searchParameters);
      const newTemplateMetadata = await getTemplateMetadata(newRunConfiguration);
      const newTemplateMetadataCompared = newIsCompareMode ? await getTemplateMetadata(newCompareToRunConfiguration) : initialValues.state.templateMetadataCompared;

      let newFilterSelections;
      if (isInitialLoad) {
        newFilterSelections = syncFilterSelectionsToSearchParams(newFilterOptions, existingFilterSelections).newFilterSelections;
      } else {
        newFilterSelections = initialValues.state.filterSelections;
      }

      const newZipDetailsResponse = await getNewZipDetails(newProject, newPerspective, 0, undefined, newFilterSelections, newRunConfiguration, newCompareToRunConfiguration);
      const newCalculationMethod = await getCalculationMethod(topConfig, newZipDetailsResponse[0]?.data, newProject);
      const newCalculationMethodCompared = !!bottomConfig ? await getCalculationMethod(bottomConfig, newZipDetailsResponse[1]?.data, newProject) : initialValues.state.calculationMethodCompared;
      const newZipExpansions = formatExpansions(newZipDetailsResponse[0]?.specialtyLevelResults);
      const newZipExpansionsCompared = formatExpansions(newZipDetailsResponse[1]?.specialtyLevelResults);
      const newTrendChartData = await getNewTrendChartData(newPerspective, newProject, getSearchParametersFromFilterSelections(newFilterSelections));
      const newSpecialtyExceptionsMap = await getNewSpecialtyExceptionsMap(newProject.projectId, newRunConfiguration.projectRunConfigurationId, newCompareToRunConfiguration?.projectRunConfigurationId || newDualMembershipRunConfiguration?.projectRunConfigurationId);
      const newNcpStatusObject = enabledFeatureFlags.has(Features.ZIP_NEXT_CLOSEST_PROVIDERS)
        ? await getProjectRunStatus(getLatestRunConfiguration(newProject).projectRunConfigurationId, ProjectRunStatusTypeEnum.NcpStatus)
        : { status: ProjectRunConfigurationStatus.Unknown, statusSeverity: StatusSeverity.Informational, statusText: "Unknown" };

      const newIsDrilledIn = getIsDrilledIn(newZipDetailsResponse[0]?.data, newPerspective);

      const newAdequacyFilters = {
        ...state.adequacyFilters,
        excludeZeroMembership: false,
        viewBy: newPerspective,
      };

      const newUrlSearchString = getUrlSearchString(newFilterSelections, newAdequacyFilters, newRunConfiguration?.projectRunConfigurationId, newCompareToRunConfiguration?.projectRunConfigurationId);
      urlShouldReplace.current = !!project && !!runConfiguration;

      const payload: ChangePerspectiveAction = {
        adequacyFilters: newAdequacyFilters,
        calculationMethod: newCalculationMethod,
        calculationMethodCompared: newCalculationMethodCompared,
        compareToRunConfiguration: newCompareToRunConfiguration,
        dualMembershipRunConfiguration: newDualMembershipRunConfiguration,
        filterOptions: newFilterOptions,
        filterSelections: newFilterSelections,
        filterSelectionsTemp: newFilterSelections,
        hasExpandableSpecialties: newZipDetailsResponse[0]?.hasExpandableSpecialties || newZipDetailsResponse[1]?.hasExpandableSpecialties,
        isCompareMode: newIsCompareMode,
        isDrilledIn: newIsDrilledIn,
        loading: false,
        module: "Adequacy",
        ncpProjectRunStatus: newNcpStatusObject.status,
        perspective: newPerspective,
        project: newProject,
        regionSetIds: initialValues.state.regionSetIds,
        // something like this will be used for zip class, but not rssg specifically. tbd.
        regionSetSpecialtyGroupToPartsLookup: state.regionSetSpecialtyGroupToPartsLookup,
        runConfiguration: newRunConfiguration,
        selectedRows: [],
        setShouldRefreshMarketProviders: () => null,
        setShouldRefreshNetworkProviders: () => null,
        setShouldRefreshProspectProviders: () => null,
        shouldShowSummariesTab: getShouldShowSummariesTab(newRunConfiguration, newFilterSelections),
        skip: 0,
        sortOption: initialValues.state.sortOption,
        specialtyExceptionsMap: newSpecialtyExceptionsMap,
        templateMetadata: newTemplateMetadata,
        templateMetadataCompared: newTemplateMetadataCompared,
        totalNumberOfRecords: newZipDetailsResponse[0]?.totalNumberOfRecords,
        totalNumberOfRecordsSecondary: newZipDetailsResponse[1]?.totalNumberOfRecords,
        trendChartData: newTrendChartData,
        urlSearchString: newUrlSearchString,
        zipClassDrillInDetails: null,
        zipDetails: newZipDetailsResponse[0]?.data,
        zipDetailsCompared: newZipDetailsResponse[1]?.data,
        zipExpansions: newZipExpansions,
        zipExpansionsCompared: newZipExpansionsCompared,
      };

      dispatch({
        payload,
        type: ZipProjectActionsEnum.ChangePerspectiveZipClass,
      });
    } catch (error) {
      dispatch({
        payload: { error: error, loading: false },
        type: ZipProjectActionsEnum.Error
      });
    }
  }

  const onChangePerspectiveToZipCode = async (project?: Project, runConfiguration?: ProjectRunConfiguration, compareToRunConfiguration?: ProjectRunConfiguration) => {
    try {
      dispatch({
        payload: { loading: true },
        type: ZipProjectActionsEnum.Loading,
      });

      const newPerspective = ProjectPerspective.ByZipCode;
      const newProject = project || state.project;
      const newRunConfiguration = runConfiguration || state.runConfiguration;
      // compareToRunConfiguration is only supplied on initial load (e.g. restored from the URL after
      // "Return to Project"); otherwise fall back to whatever compare selection is already in state.
      const newCompareToRunConfiguration = compareToRunConfiguration ?? state.compareToRunConfiguration;
      const newIsCompareMode = !!newCompareToRunConfiguration;
      const { topConfig, bottomConfig } = getTopAndBottomRunConfigurations(newRunConfiguration, newCompareToRunConfiguration, newIsCompareMode, state.isDualMembershipMode);
      const isInitialLoad = !!project && !!runConfiguration;
      const newDualMembershipRunConfiguration = newProject.projectRunConfigurations.find((rc) => rc.projectRunConfigurationId === newRunConfiguration.companionProjectRunConfigurationId);

      const searchParameters = parseQuerystring(history.location.search);
      const newFilterOptions = await getNewFilterOptions(newPerspective, newProject.projectId, newRunConfiguration, newCompareToRunConfiguration);
      const existingFilterSelections = getFilterSelectionsFromSearchParameters(searchParameters);
      const newTemplateMetadata = await getTemplateMetadata(newRunConfiguration);
      const newTemplateMetadataCompared = newIsCompareMode ? await getTemplateMetadata(newCompareToRunConfiguration) : initialValues.state.templateMetadataCompared;

      let newFilterSelections;
      if (isInitialLoad) {
        newFilterSelections = syncFilterSelectionsToSearchParams(newFilterOptions, existingFilterSelections).newFilterSelections;
      } else {
        newFilterSelections = initialValues.state.filterSelections;
      }

      const filteredRegionSetIdsPromise = getFilteredZipCodeRegionSetIds(newPerspective, newProject.projectId, newRunConfiguration.projectRunConfigurationId, newFilterSelections);
      const newZipDetailsResponse = await getNewZipDetails(newProject, newPerspective, 0, undefined, newFilterSelections, newRunConfiguration, newCompareToRunConfiguration);
      const newCalculationMethod = await getCalculationMethod(topConfig, newZipDetailsResponse[0]?.data, newProject);
      const newCalculationMethodCompared = await getCalculationMethod(bottomConfig, newZipDetailsResponse[1]?.data, newProject);
      const newIsDrilledIn = getIsDrilledIn(newZipDetailsResponse[0]?.data, newPerspective);
      const newRssgLookup = getRegionSetSpecialtyGroupIdLookup(newZipDetailsResponse[0]?.data, newZipDetailsResponse[1]?.data);
      const newZipExpansions = formatExpansions(newZipDetailsResponse[0]?.specialtyLevelResults);
      const newZipExpansionsCompared = formatExpansions(newZipDetailsResponse[1]?.specialtyLevelResults);
      const newTrendChartData = await getNewTrendChartData(newPerspective, newProject, getSearchParametersFromFilterSelections(newFilterSelections));
      const newSpecialtyExceptionsMap = await getNewSpecialtyExceptionsMap(newProject.projectId, newRunConfiguration.projectRunConfigurationId, newCompareToRunConfiguration?.projectRunConfigurationId || newDualMembershipRunConfiguration?.projectRunConfigurationId);
      const newNcpStatusObject = enabledFeatureFlags.has(Features.ZIP_NEXT_CLOSEST_PROVIDERS)
        ? await getProjectRunStatus(getLatestRunConfiguration(newProject).projectRunConfigurationId, ProjectRunStatusTypeEnum.NcpStatus)
        : { status: ProjectRunConfigurationStatus.Unknown, statusSeverity: StatusSeverity.Informational, statusText: "Unknown" };

      const newAdequacyFilters = {
        ...state.adequacyFilters,
        excludeZeroMembership: false,
        viewBy: newPerspective,
      };

      const newUrlSearchString = getUrlSearchString(newFilterSelections, newAdequacyFilters, newRunConfiguration?.projectRunConfigurationId, newCompareToRunConfiguration?.projectRunConfigurationId);
      urlShouldReplace.current = isInitialLoad;

      const classificationFilter = structuredClone(newFilterSelections);
      classificationFilter.zipClass = newFilterOptions.zipClass.find((zc) => zc.key === newZipDetailsResponse[0]?.data[0]?.zipClass);
      const newZipClassDrillInData = newIsDrilledIn && state.perspective === ProjectPerspective.ByZipCode
        ? await getZipClassAnalysisResults(newProject, 0, undefined, classificationFilter, newRunConfiguration)
        : null;

      const payload: ChangePerspectiveAction = {
        adequacyFilters: newAdequacyFilters,
        calculationMethod: newCalculationMethod,
        calculationMethodCompared: newCalculationMethodCompared,
        compareToRunConfiguration: newCompareToRunConfiguration,
        dualMembershipRunConfiguration: newDualMembershipRunConfiguration,
        filterOptions: newFilterOptions,
        filterSelections: newFilterSelections,
        filterSelectionsTemp: newFilterSelections,
        hasExpandableSpecialties: newZipDetailsResponse[0]?.hasExpandableSpecialties || newZipDetailsResponse[1]?.hasExpandableSpecialties,
        isCompareMode: newIsCompareMode,
        isDrilledIn: newIsDrilledIn,
        loading: false,
        module: "Adequacy",
        ncpProjectRunStatus: newNcpStatusObject.status,
        perspective: newPerspective,
        project: newProject,
        regionSetIds: await filteredRegionSetIdsPromise,
        regionSetSpecialtyGroupToPartsLookup: newRssgLookup,
        runConfiguration: newRunConfiguration,
        selectedRows: [],
        setShouldRefreshMarketProviders: setShouldRefreshMarketProviders,
        setShouldRefreshNetworkProviders: setShouldRefreshNetworkProviders,
        setShouldRefreshProspectProviders: setShouldRefreshProspectProviders,
        shouldShowSummariesTab: getShouldShowSummariesTab(newRunConfiguration, newFilterSelections),
        skip: 0,
        sortOption: initialValues.state.sortOption,
        specialtyExceptionsMap: newSpecialtyExceptionsMap,
        templateMetadata: newTemplateMetadata,
        templateMetadataCompared: newTemplateMetadataCompared,
        totalNumberOfRecords: newZipDetailsResponse[0]?.totalNumberOfRecords,
        totalNumberOfRecordsSecondary: newZipDetailsResponse[1]?.totalNumberOfRecords,
        trendChartData: newTrendChartData,
        urlSearchString: newUrlSearchString,
        zipClassDrillInDetails: newZipClassDrillInData?.[0]?.data as ZipClassAggregateResultDisplayModel[],
        zipDetails: newZipDetailsResponse[0]?.data,
        zipDetailsCompared: newZipDetailsResponse[1]?.data,
        zipExpansions: newZipExpansions,
        zipExpansionsCompared: newZipExpansionsCompared,
      };

      dispatch({
        payload,
        type: ZipProjectActionsEnum.ChangePerspectiveZipCode,
      });
    } catch (error) {
      dispatch({
        payload: { error: error, loading: false },
        type: ZipProjectActionsEnum.Error
      });
    }
  }

  const onChangeProjectStatus = async (messagePayload: ZipCodeAnalysisStatusChangeNotificationMessagePayload) => {
    try {
      dispatch({
        payload: { loading: true },
        type: ZipProjectActionsEnum.Loading,
      });

      const newProject = await getProjectData(messagePayload.projectId.toString());
      const newRunConfiguration = newProject.projectRunConfigurations.find((prc) => prc.projectRunConfigurationId === messagePayload.projectRunConfigurationId) || getDefaultRunConfiguration(newProject);
      const newFilterOptions = await getNewFilterOptions(state.perspective, newProject.projectId, newRunConfiguration, state.compareToRunConfiguration);
      const newZipDetailsResponse = await getNewZipDetails(newProject, undefined, 0, initialValues.state.sortOption, initialValues.state.filterSelections, newRunConfiguration);
      const newRssgLookup = getRegionSetSpecialtyGroupIdLookup(newZipDetailsResponse[0]?.data, newZipDetailsResponse[1]?.data);
      // reset filters to default in case the service area or specialties changed
      const newFilterSelections = initialValues.state.filterSelections;
      const newTemplateMetadata = await getTemplateMetadata(newRunConfiguration);
      const newZipExpansions = formatExpansions(newZipDetailsResponse[0]?.specialtyLevelResults);
      const newZipExpansionsCompared = formatExpansions(newZipDetailsResponse[1]?.specialtyLevelResults);
      const newTrendChartData = await getNewTrendChartData(state.perspective, newProject, getSearchParametersFromFilterSelections(newFilterSelections));
      const newSpecialtyExceptionsMap = await getNewSpecialtyExceptionsMap(newProject.projectId, newRunConfiguration.projectRunConfigurationId, state.compareToRunConfiguration?.projectRunConfigurationId || state.dualMembershipRunConfiguration?.projectRunConfigurationId);
      const { topConfig, bottomConfig } = getTopAndBottomRunConfigurations(newRunConfiguration, state.compareToRunConfiguration, state.isCompareMode, state.isDualMembershipMode);
      const newCalculationMethod = await getCalculationMethod(topConfig, newZipDetailsResponse[0]?.data);
      const newCalculationMethodCompared = await getCalculationMethod(bottomConfig, newZipDetailsResponse[1]?.data);
      const newIsRunning = isProjectRunning(newProject);

      dispatch({
        payload: {
          calculationMethod: newCalculationMethod,
          calculationMethodCompared: newCalculationMethodCompared,
          filterOptions: newFilterOptions,
          filterSelections: newFilterSelections,
          filterSelectionsTemp: newFilterSelections,
          hasExpandableSpecialties: newZipDetailsResponse[0]?.hasExpandableSpecialties || newZipDetailsResponse[1]?.hasExpandableSpecialties,
          isDrilledIn: false,
          loading: false,
          project: {
            ...newProject,
            isEditRestricted: newIsRunning,
            isReadOnly: newIsRunning,
            isRunning: newIsRunning,
          },
          regionSetIds: initialValues.state.regionSetIds,
          regionSetSpecialtyGroupToPartsLookup: newRssgLookup,
          runConfiguration: newRunConfiguration,
          selectedRows: initialValues.state.selectedRows,
          skip: initialValues.state.skip,
          specialtyExceptionsMap: newSpecialtyExceptionsMap,
          templateMetadata: newTemplateMetadata,
          totalNumberOfRecords: newZipDetailsResponse[0]?.totalNumberOfRecords,
          totalNumberOfRecordsSecondary: newZipDetailsResponse[1]?.totalNumberOfRecords,
          trendChartData: newTrendChartData,
          urlSearchString: getUrlSearchString(newFilterSelections, state.adequacyFilters, newRunConfiguration?.projectRunConfigurationId, state.compareToRunConfiguration?.projectRunConfigurationId),
          zipDetails: newZipDetailsResponse[0]?.data,
          zipDetailsCompared: newZipDetailsResponse[1]?.data,
          zipExpansions: newZipExpansions,
          zipExpansionsCompared: newZipExpansionsCompared,
        },
        type: ZipProjectActionsEnum.ChangeProjectStatus,
      });
    } catch (error) {
      dispatch({
        payload: { error: error, loading: false },
        type: ZipProjectActionsEnum.Error
      });
    }
  }

  const onFinishProcessingException = async (newProject: Project, newRunConfiguration: ProjectRunConfiguration) => {
    try {
      dispatch({
        payload: { loading: true },
        type: ZipProjectActionsEnum.Loading,
      });

      const newSkip = 0;
      const newSort = initialValues.state.sortOption;
      const newCompareToRunConfiguration: ProjectRunConfiguration = undefined;

      const newFilterOptions = await getNewFilterOptions(state.perspective, newProject.projectId, newRunConfiguration, newCompareToRunConfiguration);

      const newFilterSelections: ZipAnalysisSearchSelections = {
        city: state.filterSelections.city,
        county: newFilterOptions.county.find((s) => s.key === state.filterSelections.county.key) ? state.filterSelections.county : initialValues.state.filterSelections.county,
        metOverall: newFilterOptions.metOverall.find((s) => s.key === state.filterSelections.metOverall.key) ? state.filterSelections.metOverall : initialValues.state.filterSelections.metOverall,
        metPassPercent: newFilterOptions.metPassPercent.find((s) => s.key === state.filterSelections.metPassPercent.key) ? state.filterSelections.metPassPercent : initialValues.state.filterSelections.metPassPercent,
        specialtyGroupCode: newFilterOptions.specialtyGroupCode.find((s) => s.key === state.filterSelections.specialtyGroupCode.key) ? state.filterSelections.specialtyGroupCode : initialValues.state.filterSelections.specialtyGroupCode,
        state: newFilterOptions.state.find((s) => s.key === state.filterSelections.state.key) ? state.filterSelections.state : initialValues.state.filterSelections.state,
        zipClass: newFilterOptions.zipClass.find((s) => s.key === state.filterSelections.zipClass.key) ? state.filterSelections.zipClass : initialValues.state.filterSelections.zipClass,
        zipCode: state.filterSelections.zipCode,
      };

      let newTrendChartData = state.trendChartData;
      if (haveFilterSelectionsChanged(state.filterSelections, newFilterSelections)) {
        newTrendChartData = await getNewTrendChartData(state.perspective, newProject, getSearchParametersFromFilterSelections(newFilterSelections));
      }

      const newAdequacyFilters = { ...state.adequacyFilters, excludeZeroMembership: false };
      const newZipDetailsResponse = await getNewZipDetails(state.project, undefined, newSkip, newSort, newFilterSelections, newRunConfiguration, state.compareToRunConfiguration, undefined, newAdequacyFilters);
      const newRegionSetSpecialtyGroupToPartsLookup = getRegionSetSpecialtyGroupIdLookup(newZipDetailsResponse[0]?.data, newZipDetailsResponse[1]?.data);
      const newSearchParameterString = getUrlSearchString(newFilterSelections, state.adequacyFilters, newRunConfiguration?.projectRunConfigurationId, newCompareToRunConfiguration?.projectRunConfigurationId);
      const newTemplateMetadata = await getTemplateMetadata(newRunConfiguration);
      const newCalculationMethod = await getCalculationMethod(newRunConfiguration, newZipDetailsResponse[0]?.data);
      const newZipExpansions = formatExpansions(newZipDetailsResponse[0]?.specialtyLevelResults);
      const newRegionSetIds = await getFilteredZipCodeRegionSetIds(state.perspective, newProject.projectId, newRunConfiguration.projectRunConfigurationId, newFilterSelections);

      const newSpecialtyExceptionsMap = await getNewSpecialtyExceptionsMap(newProject.projectId, newRunConfiguration.projectRunConfigurationId, undefined, true);

      dispatch({
        payload: {
          calculationMethod: newCalculationMethod,
          calculationMethodCompared: undefined,
          compareToRunConfiguration: newCompareToRunConfiguration,
          filterOptions: newFilterOptions,
          filterSelections: newFilterSelections,
          filterSelectionsTemp: newFilterSelections,
          hasExpandableSpecialties: newZipDetailsResponse[0]?.hasExpandableSpecialties,
          isCompareMode: false,
          isDrilledIn: state.isDrilledIn,
          isDualMembershipMode: false,
          loading: false,
          project: newProject,
          regionSetIds: newRegionSetIds,
          regionSetSpecialtyGroupToPartsLookup: newRegionSetSpecialtyGroupToPartsLookup,
          runConfiguration: newRunConfiguration,
          specialtyExceptionsMap: newSpecialtyExceptionsMap,
          templateMetadata: newTemplateMetadata,
          templateMetadataCompared: undefined,
          totalNumberOfRecords: newZipDetailsResponse[0]?.totalNumberOfRecords,
          totalNumberOfRecordsSecondary: newZipDetailsResponse[1]?.totalNumberOfRecords,
          trendChartData: newTrendChartData,
          urlSearchString: newSearchParameterString,
          zipDetails: newZipDetailsResponse[0]?.data,
          zipDetailsCompared: newZipDetailsResponse[1]?.data,
          zipExpansions: newZipExpansions,
          zipExpansionsCompared: formatExpansions(newZipDetailsResponse[1]?.specialtyLevelResults),
        },
        type: ZipProjectActionsEnum.FinishProcessingException,
      })
    } catch (error) {
      dispatch({
        payload: { error: error, loading: false },
        type: ZipProjectActionsEnum.Error
      });
    }
  }

  const onChangeRunConfiguration = async (newRunConfigurationId: number) => {
    try {
      dispatch({
        payload: { loading: true },
        type: ZipProjectActionsEnum.Loading
      });

      const newSkip = 0;
      const newSort = initialValues.state.sortOption;

      let newRunConfiguration = state.project.projectRunConfigurations.find((prc) => prc.projectRunConfigurationId === newRunConfigurationId);
      // the trend chart and dropdown only send primary run config ids, so if we're currently viewing the secondary membership,
      // we want to stay in the secondary membership. if we're in dual membership, this is irrelevant and will be handled farther below
      // however, if we're currently viewing the secondary membership and the new selection does not have a secondary membership, we'll stick with the primary
      if (!state.isDualMembershipMode && state.runConfiguration.projectRunConfigurationType === ProjectRunConfigurationType.DualMembershipSupplemental) {
        newRunConfiguration = state.project.projectRunConfigurations.find((prc) => prc.projectRunConfigurationId === newRunConfiguration.companionProjectRunConfigurationId) || newRunConfiguration;
      }
      const { topConfig, bottomConfig } = getTopAndBottomRunConfigurations(newRunConfiguration, state.compareToRunConfiguration, state.isCompareMode, state.isDualMembershipMode);
      const newFilterOptions = await getNewFilterOptions(state.perspective, state.project.projectId, newRunConfiguration, state.compareToRunConfiguration);

      // city and zipCode are just strings that don't need a matching key
      // the rest are DropdownItem<T>s
      const newFilters: ZipAnalysisSearchSelections = {
        city: state.filterSelections.city,
        county: newFilterOptions.county.find((s) => s.key === state.filterSelections.county.key) ? state.filterSelections.county : initialValues.state.filterSelections.county,
        metOverall: newFilterOptions.metOverall.find((s) => s.key === state.filterSelections.metOverall.key) ? state.filterSelections.metOverall : initialValues.state.filterSelections.metOverall,
        metPassPercent: newFilterOptions.metPassPercent.find((s) => s.key === state.filterSelections.metPassPercent.key) ? state.filterSelections.metPassPercent : initialValues.state.filterSelections.metPassPercent,
        specialtyGroupCode: newFilterOptions.specialtyGroupCode.find((s) => s.key === state.filterSelections.specialtyGroupCode.key) ? state.filterSelections.specialtyGroupCode : initialValues.state.filterSelections.specialtyGroupCode,
        state: newFilterOptions.state.find((s) => s.key === state.filterSelections.state.key) ? state.filterSelections.state : initialValues.state.filterSelections.state,
        zipClass: newFilterOptions.zipClass.find((s) => s.key === state.filterSelections.zipClass.key) ? state.filterSelections.zipClass : initialValues.state.filterSelections.zipClass,
        zipCode: state.filterSelections.zipCode,
      };

      // if the newFilters are different from the current selections, we need to update the trend chart items
      let newTrendChartData = state.trendChartData;
      if (haveFilterSelectionsChanged(state.filterSelections, newFilters)) {
        newTrendChartData = await getNewTrendChartData(state.perspective, state.project, getSearchParametersFromFilterSelections(newFilters));
      }

      const newAdequacyFilters = { ...state.adequacyFilters, excludeZeroMembership: false };
      const newZipDetailsResponse = await getNewZipDetails(state.project, undefined, newSkip, newSort, newFilters, newRunConfiguration, state.compareToRunConfiguration, undefined, newAdequacyFilters);
      const newRegionSetSpecialtyGroupToPartsLookup = getRegionSetSpecialtyGroupIdLookup(newZipDetailsResponse[0]?.data, newZipDetailsResponse[1]?.data);
      const newSearchParameterString = getUrlSearchString(newFilters, state.adequacyFilters, newRunConfiguration?.projectRunConfigurationId, state.compareToRunConfiguration?.projectRunConfigurationId);
      const newTemplateMetadata = await getTemplateMetadata(newRunConfiguration);
      const newCalculationMethod = await getCalculationMethod(topConfig, newZipDetailsResponse[0]?.data);
      const newCalculationMethodCompared = await getCalculationMethod(bottomConfig, newZipDetailsResponse[1]?.data);
      const newZipExpansions = formatExpansions(newZipDetailsResponse[0]?.specialtyLevelResults);
      const newZipExpansionsCompared = formatExpansions(newZipDetailsResponse[1]?.specialtyLevelResults);
      const newIsDrilledIn = getIsDrilledIn(newZipDetailsResponse[0]?.data);

      // if the new configuration is not dual membership, we need to leave dual membership mode
      const newIsDualMembershipMode = state.isDualMembershipMode && !!newRunConfiguration.companionProjectRunConfigurationId;
      // and if it is dual membership, we need to update the secondary config
      const newDualMembershipRunConfiguration = newIsDualMembershipMode ? state.project.projectRunConfigurations.find((rc) => rc.projectRunConfigurationId === newRunConfiguration.companionProjectRunConfigurationId) : null;
      const newSpecialtyExceptionsMap = await getNewSpecialtyExceptionsMap(state.project.projectId, newRunConfiguration.projectRunConfigurationId, state.compareToRunConfiguration?.projectRunConfigurationId || newDualMembershipRunConfiguration?.projectRunConfigurationId);

      dispatch({
        payload: {
          adequacyFilters: newAdequacyFilters,
          calculationMethod: newCalculationMethod,
          calculationMethodCompared: newCalculationMethodCompared,
          dualMembershipRunConfiguration: newDualMembershipRunConfiguration,
          filterOptions: newFilterOptions,
          filterSelections: newFilters,
          filterSelectionsTemp: newFilters,
          hasExpandableSpecialties: newZipDetailsResponse[0]?.hasExpandableSpecialties || newZipDetailsResponse[1]?.hasExpandableSpecialties,
          isDrilledIn: newIsDrilledIn,
          isDualMembershipMode: newIsDualMembershipMode,
          loading: false,
          regionSetIds: initialValues.state.regionSetIds,
          regionSetSpecialtyGroupToPartsLookup: newRegionSetSpecialtyGroupToPartsLookup,
          runConfiguration: newRunConfiguration,
          selectedRows: initialValues.state.selectedRows,
          shouldRefreshMarketProviders: newIsDrilledIn,
          shouldRefreshNetNewProviders: newIsDrilledIn,
          shouldRefreshNetworkProviders: newIsDrilledIn,
          shouldRefreshProspectProviders: newIsDrilledIn,
          shouldShowSummariesTab: getShouldShowSummariesTab(newRunConfiguration, newFilters),
          skip: newSkip,
          sortOption: newSort,
          specialtyExceptionsMap: newSpecialtyExceptionsMap,
          templateMetadata: newTemplateMetadata,
          totalNumberOfRecords: newZipDetailsResponse[0]?.totalNumberOfRecords,
          totalNumberOfRecordsSecondary: newZipDetailsResponse[1]?.totalNumberOfRecords,
          trendChartData: newTrendChartData,
          urlSearchString: newSearchParameterString,
          zipDetails: newZipDetailsResponse[0]?.data,
          zipDetailsCompared: newZipDetailsResponse[1]?.data,
          zipExpansions: newZipExpansions,
          zipExpansionsCompared: newZipExpansionsCompared,
        },
        type: ZipProjectActionsEnum.ChangeProjectRunConfiguration
      });
    } catch (error) {
      dispatch({
        payload: { error: error, loading: false },
        type: ZipProjectActionsEnum.Error
      });
    }
  }

  const onChangeSelectedMemberships = async (newMemberships: DropdownItem<string>[]) => {
    dispatch({
      payload: { loading: true },
      type: ZipProjectActionsEnum.Loading
    });

    // checkbox dropdown returns keys in the order selected, not the order listed
    // so if a user selected the secondary and THEN the primary, we need to make sure the primary is saved as the primary
    let newRunConfiguration = state.project.projectRunConfigurations.find((rc) => rc.projectRunConfigurationId === parseInt(newMemberships[0].key, 10));
    if (newMemberships.length > 1 && newRunConfiguration.projectRunConfigurationType === ProjectRunConfigurationType.DualMembershipSupplemental) {
      newRunConfiguration = state.project.projectRunConfigurations.find((rc) => rc.projectRunConfigurationId === newRunConfiguration.companionProjectRunConfigurationId);
    }
    const newDualMembershipRunConfiguration = newMemberships.length > 1 ? state.project.projectRunConfigurations.find((rc) => rc.projectRunConfigurationId === newRunConfiguration.companionProjectRunConfigurationId) : null;
    const newIsDualMembershipMode = !!newDualMembershipRunConfiguration;
    const newIsCompareMode = false;
    const newSpecialtyExceptionsMap = await getNewSpecialtyExceptionsMap(state.project.projectId, newRunConfiguration.projectRunConfigurationId, newDualMembershipRunConfiguration?.projectRunConfigurationId);

    let newSkip = state.skip;
    if (!newIsDualMembershipMode) {
      if (newRunConfiguration.projectRunConfigurationType === ProjectRunConfigurationType.DualMembershipPrimary) {
        if (newSkip > state.totalNumberOfRecords) {
          newSkip = Math.floor(state.totalNumberOfRecords / state.take) * state.take;
        }
      } else if (newRunConfiguration.projectRunConfigurationType === ProjectRunConfigurationType.DualMembershipSupplemental) {
        if (newSkip > state.totalNumberOfRecordsSecondary) {
          newSkip = Math.floor(state.totalNumberOfRecordsSecondary / state.take) * state.take;
        }
      }
    }

    // All of these are contingent on the user changing the selected membership
    // to one that has fewer pages than the current selection(s)
    let newZipDetailsResponse: ZipDetailsApiResponse[];
    let newZipDetails = state.zipDetails;
    let newZipDetailsCompared = state.zipDetailsCompared;
    let newZipExpansions = state.zipExpansions;
    let newZipExpansionsCompared = state.zipExpansionsCompared;
    let newHasExpandableSpecialties = state.hasExpandableSpecialties;
    let newTotalNumberOfRecords = state.totalNumberOfRecords;
    let newTotalNumberOfRecordsSecondary = state.totalNumberOfRecordsSecondary;
    if (newSkip !== state.skip) {
      newZipDetailsResponse = await getNewZipDetails(state.project, state.perspective, newSkip, state.sortOption, state.filterSelections, newRunConfiguration, newDualMembershipRunConfiguration, true);
      newZipDetails = newZipDetailsResponse[0]?.data;
      newZipDetailsCompared = newZipDetailsResponse[1]?.data;
      newHasExpandableSpecialties = newZipDetailsResponse[0]?.hasExpandableSpecialties || newZipDetailsResponse[1]?.hasExpandableSpecialties;
      newZipExpansions = formatExpansions(newZipDetailsResponse[0]?.specialtyLevelResults);
      newZipExpansionsCompared = formatExpansions(newZipDetailsResponse[1]?.specialtyLevelResults);
      newTotalNumberOfRecords = newZipDetailsResponse[0]?.totalNumberOfRecords;
      newTotalNumberOfRecordsSecondary = newZipDetailsResponse[1]?.totalNumberOfRecords;
    }

    dispatch({
      payload: {
        // Changing the membership changes the run configuration, but it's still the same run
        // and should have the same calculation method regardless
        calculationMethod: state.calculationMethod,
        calculationMethodCompared: newIsDualMembershipMode ? state.calculationMethod : initialValues.state.calculationMethodCompared,
        compareToRunConfiguration: initialValues.state.compareToRunConfiguration,
        dualMembershipRunConfiguration: newDualMembershipRunConfiguration,
        filterOptions: state.filterOptions,
        filterSelections: state.filterSelections,
        filterSelectionsTemp: state.filterSelectionsTemp,
        hasExpandableSpecialties: newHasExpandableSpecialties,
        isCompareMode: newIsCompareMode,
        isDrilledIn: state.isDrilledIn,
        isDualMembershipMode: newIsDualMembershipMode,
        loading: false,
        regionSetIds: state.regionSetIds,
        regionSetSpecialtyGroupToPartsLookup: state.regionSetSpecialtyGroupToPartsLookup,
        runConfiguration: newRunConfiguration,
        skip: newSkip,
        specialtyExceptionsMap: newSpecialtyExceptionsMap,
        // templateMetadata should be the same between two memberships on the same run
        templateMetadata: state.templateMetadata,
        templateMetadataCompared: newIsDualMembershipMode ? state.templateMetadata : initialValues.state.templateMetadata,
        totalNumberOfRecords: newTotalNumberOfRecords,
        totalNumberOfRecordsSecondary: newTotalNumberOfRecordsSecondary,
        trendChartData: state.trendChartData,
        zipDetails: newZipDetails,
        zipDetailsCompared: newZipDetailsCompared,
        zipExpansions: newZipExpansions,
        zipExpansionsCompared: newZipExpansionsCompared,
      },
      type: ZipProjectActionsEnum.ChangeMemberships,
    })
  }

  const onLoadPage = async (projectId: string) => {
    try {
      if (lastLoadedProjectId !== projectId) {
        setLastVisitedZipModule(undefined);
        setLastAppliedMembership(undefined);
      }
      lastLoadedProjectId = projectId;

      dispatch({
        payload: { loading: true },
        type: ZipProjectActionsEnum.Loading,
      });

      const newProject = await getProjectData(projectId);
      newProject.hasUnprocessedSpecialtyGroupExceptions = newProject.projectRunConfigurations?.some(r => (r.isLatestPublished || r.datePublished === null) && r.hasUnprocessedSpecialtyGroupExceptions) ?? false;

      const searchParameters = parseQuerystring(history.location.search);
      // Restore the timeslices the user was viewing (e.g. after "Return to Project" from a report)
      // from the URL. Falls back to the default run configuration when the URL has no (or an invalid)
      // resultsAsOf, which is the case for plain navigation to the project.
      const { runConfiguration: restoredRunConfiguration, compareToRunConfiguration: restoredCompareToRunConfiguration } = getRunConfigurationsFromSearchParams(searchParameters, newProject.projectRunConfigurations);
      const newRunConfiguration = restoredRunConfiguration ?? getDefaultRunConfiguration(newProject);

      if (!!newRunConfiguration) {
        switch (searchParameters.viewBy) {
          case ProjectPerspective.ByZipClass:
            onChangePerspectiveToZipClass(newProject, newRunConfiguration, restoredCompareToRunConfiguration);
            break;
          case ProjectPerspective.ByZipCode:
          default:
            onChangePerspectiveToZipCode(newProject, newRunConfiguration, restoredCompareToRunConfiguration);
            break;
        }
      } else {
        dispatch({
          payload: {
            loading: false,
            ncpProjectRunStatus: ProjectRunConfigurationStatus.Unknown,
            project: newProject
          },
          type: ZipProjectActionsEnum.LoadProjectWithoutRunConfiguration,
        });
      }
    } catch (error) {
      dispatch({
        payload: { error: error, loading: false },
        type: ZipProjectActionsEnum.Error
      });
    }
  };

  const onPublishTimeSlice = async (comment?: string) => {
    try {
      dispatch({
        payload: { loading: true },
        type: ZipProjectActionsEnum.Loading,
      });
      await zipProjectService.publishZipProject(state.project.projectId, comment);

      const newProject = await getProjectData(state.project.projectId.toString());
      const newRunConfiguration = getDefaultRunConfiguration(newProject);
      const newTemplateMetadata = await getTemplateMetadata(newRunConfiguration);
      const newTrendChartData = await getNewTrendChartData(state.perspective, newProject, getSearchParametersFromFilterSelections(state.filterSelections));

      dispatch({
        payload: {
          loading: false,
          project: newProject,
          runConfiguration: newRunConfiguration,
          shouldShowSummariesTab: getShouldShowSummariesTab(state.runConfiguration, state.filterSelections),
          templateMetadata: newTemplateMetadata,
          trendChartData: newTrendChartData,
        },
        type: ZipProjectActionsEnum.PublishProjectRunConfiguration,
      });
    } catch (error) {
      dispatch({
        payload: { error: error, loading: false },
        type: ZipProjectActionsEnum.Error
      });
    }
  }

  const onApplyFilters = async () => {
    try {
      dispatch({
        payload: { loading: true },
        type: ZipProjectActionsEnum.Loading,
      });

      const newFilterSelections = state.filterSelectionsTemp;
      const filteredRegionSetIds = await getFilteredZipCodeRegionSetIds(state.perspective, state.project.projectId, state.runConfiguration.projectRunConfigurationId, newFilterSelections);
      const newAdequacyFilters = { ...state.adequacyFilters, excludeZeroMembership: false };
      const newZipDetailsResponse = await getNewZipDetails(state.project, undefined, 0, undefined, newFilterSelections, undefined, undefined, undefined, newAdequacyFilters);
      const newRssgLookup = getRegionSetSpecialtyGroupIdLookup(newZipDetailsResponse[0]?.data, newZipDetailsResponse[1]?.data);
      const newExpansions = formatExpansions(newZipDetailsResponse[0]?.specialtyLevelResults);
      const newExpansionsCompared = formatExpansions(newZipDetailsResponse[1]?.specialtyLevelResults);
      const newTrendChartData = await getNewTrendChartData(state.perspective, state.project, getSearchParametersFromFilterSelections(newFilterSelections));

      const search = parseQuerystring(history.location.search);
      Object.keys(newFilterSelections).forEach((sf) => {
        const filter = newFilterSelections[sf as keyof ZipAnalysisSearchSelections];
        const filterValue = (typeof filter === "string") ? filter : (filter as DropdownItem<string>)?.key;
        if (!!filterValue && filterValue !== "all" && filterValue !== "") {
          search[sf] = filterValue;
        } else {
          delete search[sf];
        }
      });
      const newIsDrilledIn = getIsDrilledIn(newZipDetailsResponse[0]?.data);

      const classificationFilter = structuredClone(newFilterSelections);
      classificationFilter.zipClass = state.filterOptions.zipClass.find((zc) => zc.key === newZipDetailsResponse[0]?.data[0]?.zipClass);
      const newZipClassDrillInData = newIsDrilledIn && state.perspective === ProjectPerspective.ByZipCode
        ? await getZipClassAnalysisResults(state.project, 0, initialValues.state.sortOption, classificationFilter, state.runConfiguration)
        : null;

      const { topConfig, bottomConfig } = getTopAndBottomRunConfigurations(state.runConfiguration, state.compareToRunConfiguration, state.isCompareMode, state.isDualMembershipMode);
      const newCalculationMethod = await getCalculationMethod(topConfig, newZipDetailsResponse[0]?.data);
      const newCalculationMethodCompared = await getCalculationMethod(bottomConfig, newZipDetailsResponse[1]?.data);

      dispatch({
        payload: {
          adequacyFilters: newAdequacyFilters,
          calculationMethod: newCalculationMethod,
          calculationMethodCompared: newCalculationMethodCompared,
          filterSelections: newFilterSelections,
          filterSelectionsTemp: newFilterSelections,
          hasExpandableSpecialties: newZipDetailsResponse[0]?.hasExpandableSpecialties || newZipDetailsResponse[1]?.hasExpandableSpecialties,
          isDrilledIn: newIsDrilledIn,
          loading: false,
          regionSetIds: filteredRegionSetIds,
          regionSetSpecialtyGroupToPartsLookup: newRssgLookup,
          selectedRows: [],
          shouldRefreshMarketProviders: newIsDrilledIn,
          shouldRefreshNetNewProviders: newIsDrilledIn,
          shouldRefreshNetworkProviders: newIsDrilledIn,
          shouldRefreshProspectProviders: newIsDrilledIn,
          shouldShowSummariesTab: getShouldShowSummariesTab(state.runConfiguration, newFilterSelections),
          skip: 0,
          totalNumberOfRecords: newZipDetailsResponse[0]?.totalNumberOfRecords,
          totalNumberOfRecordsSecondary: newZipDetailsResponse[1]?.totalNumberOfRecords,
          trendChartData: newTrendChartData,
          urlSearchString: querystringify(search),
          zipClassDrillInDetails: newZipClassDrillInData?.[0]?.data as ZipClassAggregateResultDisplayModel[],
          zipDetails: newZipDetailsResponse[0]?.data,
          zipDetailsCompared: newZipDetailsResponse[1]?.data,
          zipExpansions: newExpansions,
          zipExpansionsCompared: newExpansionsCompared,
        },
        type: ZipProjectActionsEnum.ApplyFilters,
      });
    } catch (error) {
      dispatch({
        payload: { error: error, loading: false },
        type: ZipProjectActionsEnum.Error
      });
    }
  }

  const onResetFilters = async () => {
    try {
      dispatch({
        payload: { loading: true },
        type: ZipProjectActionsEnum.Loading,
      });

      const newFilterSelections = initialValues.state.filterSelections;
      const newAdequacyFilters = { ...state.adequacyFilters, excludeZeroMembership: false };
      const sortOptionForFetch = state.isDrilledIn ? state.sortOption : initialValues.state.sortOption;
      const newZipDetailsResponse = await getNewZipDetails(state.project, undefined, 0, sortOptionForFetch, newFilterSelections, undefined, undefined, undefined, newAdequacyFilters);
      const newExpansions = formatExpansions(newZipDetailsResponse[0]?.specialtyLevelResults);
      const newExpansionsCompared = formatExpansions(newZipDetailsResponse[1]?.specialtyLevelResults);
      const newRssgLookup = getRegionSetSpecialtyGroupIdLookup(newZipDetailsResponse[0]?.data, newZipDetailsResponse[1]?.data);
      const newTrendChartData = await getNewTrendChartData(state.perspective, state.project, getSearchParametersFromFilterSelections(newFilterSelections));

      const search = parseQuerystring(history.location.search);
      Object.keys(newFilterSelections).forEach((sf) => {
        const filter = newFilterSelections[sf as keyof ZipAnalysisSearchSelections];
        const filterValue = (typeof filter === "string") ? filter : (filter as DropdownItem<string>)?.key;
        if (!!filterValue && filterValue !== "all" && filterValue !== "") {
          search[sf] = filterValue;
        } else {
          delete search[sf];
        }
      });
      const newIsDrilledIn = getIsDrilledIn(newZipDetailsResponse[0]?.data);

      const classificationFilter = structuredClone(newFilterSelections);
      classificationFilter.zipClass = state.filterOptions.zipClass.find((zc) => zc.key === newZipDetailsResponse[0]?.data[0]?.zipClass);
      const newZipClassDrillInData = newIsDrilledIn && state.perspective === ProjectPerspective.ByZipCode
        ? await getZipClassAnalysisResults(state.project, 0, initialValues.state.sortOption, classificationFilter, state.runConfiguration)
        : null;

      const { topConfig, bottomConfig } = getTopAndBottomRunConfigurations(state.runConfiguration, state.compareToRunConfiguration, state.isCompareMode, state.isDualMembershipMode);
      const newCalculationMethod = await getCalculationMethod(topConfig, newZipDetailsResponse[0]?.data);
      const newCalculationMethodCompared = await getCalculationMethod(bottomConfig, newZipDetailsResponse[1]?.data);

      dispatch({
        payload: {
          adequacyFilters: newAdequacyFilters,
          calculationMethod: newCalculationMethod,
          calculationMethodCompared: newCalculationMethodCompared,
          filterSelections: newFilterSelections,
          filterSelectionsTemp: newFilterSelections,
          hasExpandableSpecialties: newZipDetailsResponse[0]?.hasExpandableSpecialties || newZipDetailsResponse[1]?.hasExpandableSpecialties,
          isDrilledIn: newIsDrilledIn,
          loading: false,
          regionSetIds: initialValues.state.regionSetIds,
          regionSetSpecialtyGroupToPartsLookup: newRssgLookup,
          selectedRows: [],
          shouldRefreshMarketProviders: newIsDrilledIn,
          shouldRefreshNetworkProviders: newIsDrilledIn,
          shouldRefreshProspectProviders: newIsDrilledIn,
          shouldShowSummariesTab: getShouldShowSummariesTab(state.runConfiguration, newFilterSelections),
          skip: 0,
          sortOption: state.isDrilledIn ? state.sortOption : initialValues.state.sortOption,
          sortResetCount: state.isDrilledIn ? state.sortResetCount : state.sortResetCount + 1,
          totalNumberOfRecords: newZipDetailsResponse[0]?.totalNumberOfRecords,
          totalNumberOfRecordsSecondary: newZipDetailsResponse[1]?.totalNumberOfRecords,
          trendChartData: newTrendChartData,
          urlSearchString: querystringify(search),
          zipClassDrillInDetails: newZipClassDrillInData?.[0]?.data as ZipClassAggregateResultDisplayModel[],
          zipDetails: newZipDetailsResponse[0]?.data,
          zipDetailsCompared: newZipDetailsResponse[1]?.data,
          zipExpansions: newExpansions,
          zipExpansionsCompared: newExpansionsCompared,
        },
        type: ZipProjectActionsEnum.ResetFilters,
      });
    } catch (error) {
      dispatch({
        payload: { error: error, loading: false },
        type: ZipProjectActionsEnum.Error
      });
    }
  }

  const onChangeTablePage = async (newSkip: number) => {
    try {
      const newZipDetailsResponse = await getNewZipDetails(state.project, undefined, newSkip);
      const newExpansions = formatExpansions(newZipDetailsResponse[0]?.specialtyLevelResults);
      const newExpansionsCompared = formatExpansions(newZipDetailsResponse[1]?.specialtyLevelResults);
      const newRssgLookup = getRegionSetSpecialtyGroupIdLookup(newZipDetailsResponse[0]?.data, newZipDetailsResponse[1]?.data);
      const { topConfig, bottomConfig } = getTopAndBottomRunConfigurations(state.runConfiguration, state.compareToRunConfiguration, state.isCompareMode, state.isDualMembershipMode);
      const newCalculationMethod = await getCalculationMethod(topConfig, newZipDetailsResponse[0]?.data);
      const newCalculationMethodCompared = await getCalculationMethod(bottomConfig, newZipDetailsResponse[1]?.data);
      const newIsDrilledIn = getIsDrilledIn(newZipDetailsResponse[0]?.data, state.perspective);
      const newFilterSelectionsTemp = state.filterSelections;

      dispatch({
        payload: {
          calculationMethod: newCalculationMethod,
          calculationMethodCompared: newCalculationMethodCompared,
          filterSelectionsTemp: newFilterSelectionsTemp,
          hasExpandableSpecialties: newZipDetailsResponse[0]?.hasExpandableSpecialties || newZipDetailsResponse[1]?.hasExpandableSpecialties,
          isDrilledIn: newIsDrilledIn,
          loading: false,
          regionSetSpecialtyGroupToPartsLookup: newRssgLookup,
          skip: newSkip,
          // number of records shouldn't change from a page change
          // but if the two memberships have different numbers of pages,
          // and the selected page is higher than the other membership's total,
          // we'd risk saving undefined here
          totalNumberOfRecords: state.totalNumberOfRecords,
          totalNumberOfRecordsSecondary: state.totalNumberOfRecordsSecondary,
          trendChartData: state.trendChartData,
          zipDetails: newZipDetailsResponse[0]?.data,
          zipDetailsCompared: newZipDetailsResponse[1]?.data,
          zipExpansions: newExpansions,
          zipExpansionsCompared: newExpansionsCompared,
        },
        type: ZipProjectActionsEnum.ChangePage,
      });
    } catch (error) {
      dispatch({
        payload: { error: error, loading: false },
        type: ZipProjectActionsEnum.Error
      });
    }
  };

  const onSortTable = async (newColumn: keyof ZipCodeTableData | keyof ZipClassTableData, newDirection: SortDirections) => {
    try {
      dispatch({
        payload: { loading: true },
        type: ZipProjectActionsEnum.Loading,
      });

      const sortedColumn = getSortedColumnFromEnum(newColumn, state.perspective);
      const newSortOption: ZipAnalysisSortOption = {
        column: sortedColumn,
        direction: newDirection,
      };
      const newZipDetailsResponse = await getNewZipDetails(state.project, undefined, 0, newSortOption);
      const newExpansions = formatExpansions(newZipDetailsResponse[0]?.specialtyLevelResults);
      const newExpansionsCompared = formatExpansions(newZipDetailsResponse[1]?.specialtyLevelResults);
      const newRssgLookup = getRegionSetSpecialtyGroupIdLookup(newZipDetailsResponse[0]?.data, newZipDetailsResponse[1]?.data);
      const { topConfig, bottomConfig } = getTopAndBottomRunConfigurations(state.runConfiguration, state.compareToRunConfiguration, state.isCompareMode, state.isDualMembershipMode);
      const newCalculationMethod = await getCalculationMethod(topConfig, newZipDetailsResponse[0]?.data);
      const newCalculationMethodCompared = await getCalculationMethod(bottomConfig, newZipDetailsResponse[1]?.data);
      const newIsDrilledIn = getIsDrilledIn(newZipDetailsResponse[0]?.data, state.perspective);

      dispatch({
        payload: {
          calculationMethod: newCalculationMethod,
          calculationMethodCompared: newCalculationMethodCompared,
          hasExpandableSpecialties: newZipDetailsResponse[0]?.hasExpandableSpecialties || newZipDetailsResponse[1]?.hasExpandableSpecialties,
          isDrilledIn: newIsDrilledIn,
          loading: false,
          regionSetSpecialtyGroupToPartsLookup: newRssgLookup,
          skip: 0,
          sortOption: newSortOption,
          totalNumberOfRecords: newZipDetailsResponse[0]?.totalNumberOfRecords,
          totalNumberOfRecordsSecondary: newZipDetailsResponse[1]?.totalNumberOfRecords,
          trendChartData: state.trendChartData,
          zipDetails: newZipDetailsResponse[0]?.data,
          zipDetailsCompared: newZipDetailsResponse[1]?.data,
          zipExpansions: newExpansions,
          zipExpansionsCompared: newExpansionsCompared,
        },
        type: ZipProjectActionsEnum.SortTable,
      });
    } catch (error) {
      dispatch({
        payload: { error: error, loading: false },
        type: ZipProjectActionsEnum.Error
      });
    }
  };

  const onClickSpecialtyGroupOrZipCode = async (filtersParams: ZipCodeFilterParams) => {
    try {
      dispatch({
        payload: { loading: true },
        type: ZipProjectActionsEnum.Loading,
      });

      const { zipCode, specialtyGroupCode, stateName, county } = filtersParams;
      const newFilterSelections = structuredClone(state.filterSelections);
      const matchingSpecialtyItem = state.filterOptions.specialtyGroupCode.find((sgc) => sgc.key === specialtyGroupCode)

      newFilterSelections.specialtyGroupCode = matchingSpecialtyItem;
      newFilterSelections.zipCode = zipCode;
      newFilterSelections.state = !!stateName ? state.filterOptions.state.find((s) => s.key === stateName) || initialValues.state.filterSelections.state : newFilterSelections.state;
      newFilterSelections.county = !!county ? state.filterOptions.county.find((c) => c.key === county) || initialValues.state.filterSelections.county : newFilterSelections.county;

      const filteredRegionSetIdsPromise = getFilteredZipCodeRegionSetIds(state.perspective, state.project.projectId, state.runConfiguration.projectRunConfigurationId, newFilterSelections);
      const newZipDetailsResponse = await getNewZipDetails(state.project, undefined, 0, initialValues.state.sortOption, newFilterSelections);
      const newRssgLookup = getRegionSetSpecialtyGroupIdLookup(newZipDetailsResponse[0]?.data, newZipDetailsResponse[1]?.data);
      const newSearchString = getUrlSearchString(newFilterSelections, state.adequacyFilters, state.runConfiguration?.projectRunConfigurationId, state.compareToRunConfiguration?.projectRunConfigurationId);
      const newShouldShowSummariesTab = getShouldShowSummariesTab(state.runConfiguration, newFilterSelections)
      const newIsDrilledIn = getIsDrilledIn(newZipDetailsResponse[0]?.data);
      const newExpansions = formatExpansions(newZipDetailsResponse[0]?.specialtyLevelResults);
      const newExpansionsCompared = formatExpansions(newZipDetailsResponse[1]?.specialtyLevelResults);
      const newTrendChartData = await getNewTrendChartData(state.perspective, state.project, getSearchParametersFromFilterSelections(newFilterSelections));

      const classificationFilter = structuredClone(newFilterSelections);
      classificationFilter.zipClass = state.filterOptions.zipClass.find((zc) => zc.key === newZipDetailsResponse[0]?.data[0]?.zipClass);
      const newZipClassDrillInData = await getZipClassAnalysisResults(state.project, 0, initialValues.state.sortOption, classificationFilter, state.runConfiguration);
      const { topConfig, bottomConfig } = getTopAndBottomRunConfigurations(state.runConfiguration, state.compareToRunConfiguration, state.isCompareMode, state.isDualMembershipMode);
      const newCalculationMethod = await getCalculationMethod(topConfig, newZipDetailsResponse[0]?.data);
      const newCalculationMethodCompared = await getCalculationMethod(bottomConfig, newZipDetailsResponse[1]?.data);

      dispatch({
        payload: {
          calculationMethod: newCalculationMethod,
          calculationMethodCompared: newCalculationMethodCompared,
          filterSelections: newFilterSelections,
          filterSelectionsTemp: newFilterSelections,
          hasExpandableSpecialties: newZipDetailsResponse[0]?.hasExpandableSpecialties || newZipDetailsResponse[1]?.hasExpandableSpecialties,
          isDrilledIn: newIsDrilledIn,
          loading: false,
          regionSetIds: await filteredRegionSetIdsPromise,
          regionSetSpecialtyGroupToPartsLookup: newRssgLookup,
          selectedRows: [],
          shouldRefreshMarketProviders: newIsDrilledIn,
          shouldRefreshNetNewProviders: newIsDrilledIn,
          shouldRefreshNetworkProviders: newIsDrilledIn,
          shouldRefreshProspectProviders: newIsDrilledIn,
          shouldShowSummariesTab: newShouldShowSummariesTab,
          skip: 0,
          totalNumberOfRecords: newZipDetailsResponse[0]?.totalNumberOfRecords,
          totalNumberOfRecordsSecondary: newZipDetailsResponse[1]?.totalNumberOfRecords,
          trendChartData: newTrendChartData,
          urlSearchString: newSearchString,
          zipClassDrillInDetails: newZipClassDrillInData?.[0]?.data as ZipClassAggregateResultDisplayModel[],
          zipDetails: newZipDetailsResponse[0]?.data,
          zipDetailsCompared: newZipDetailsResponse[1]?.data,
          zipExpansions: newExpansions,
          zipExpansionsCompared: newExpansionsCompared,
        },
        type: ZipProjectActionsEnum.ClickSpecialtyGroupOrZipCode,
      });
    } catch (error) {
      dispatch({
        payload: { error: error, loading: false },
        type: ZipProjectActionsEnum.Error
      });
    }
  }

  const onEditTimeslicePublishComment = async () => {
    try {
      const newTrendChartData = await getNewTrendChartData(state.perspective, state.project, getSearchParametersFromFilterSelections(state.filterSelections));

      dispatch({
        payload: {
          trendChartData: newTrendChartData,
        },
        type: ZipProjectActionsEnum.EditTimeslicePublishComment,
      });
    } catch (error) {
      dispatch({
        payload: { error: error, loading: false },
        type: ZipProjectActionsEnum.Error
      });
    }
  }

  //Implement back-end code to run the projection analysis on zip projects here
  const onProcessProviderChanges = async (reprocessFailure: boolean): Promise<void> => {
    if (state.project && !state.project.isRunning) {
      try {
        dispatch({
          payload: {
            project: {
              ...state.project,
              isEditRestricted: true,
              isReadOnly: true,
              isRunning: true,
            },
          },
          type: ZipProjectActionsEnum.ProcessProviderChanges,
        });

        await zipProjectService.processProviderChanges(state.project.projectId, reprocessFailure);
      } catch (error) {
        dispatch({
          payload: { error: error, loading: false },
          type: ZipProjectActionsEnum.Error
        });
      }
    }
  }

  const onProcessSpecialtyExceptions = (projectCopy: Project, runConfigCopy: ProjectRunConfiguration, notificationPayload?: ProcessingSpecialtyGroupExceptionsPayload) => {
    const newProject = projectCopy === state.project ? structuredClone(state.project) : projectCopy;
    const newRunConfiguration = runConfigCopy === state.runConfiguration ? structuredClone(runConfigCopy) : runConfigCopy;

    // set the project to readonly + edit restricted
    newProject.isReadOnly = !!notificationPayload ? notificationPayload.isReadOnly : true;
    newProject.isEditRestricted = !!notificationPayload ? notificationPayload.isEditRestricted : true;
    newProject.isRunning = true;

    // update the affected run config's status, but we don't need to switch to it yet
    if (!!notificationPayload) {
      newProject.projectRunConfigurations.find((prc) => prc.projectRunConfigurationId === notificationPayload.projectRunConfigurationId).status = notificationPayload.status;
      if (newRunConfiguration.projectRunConfigurationId === notificationPayload.projectRunConfigurationId) {
        newRunConfiguration.status = notificationPayload.status;
      }
    }

    dispatch({
      payload: {
        project: newProject,
        runConfiguration: newRunConfiguration,
      },
      type: ZipProjectActionsEnum.StartProcessingException,
    });
  }

  const onSubmitSpecialtyException = async (exception: SpecialtyGroupException) => {
    // create or update exception
    const exceptionKey = getSpecialtyExceptionKey(exception);
    const newTimesliceExceptions = state.specialtyExceptionsMap.get(state.runConfiguration.projectRunConfigurationId) ?? {};
    let newException: SpecialtyGroupException;
    if (!!(newTimesliceExceptions)[exceptionKey]) {
      // update
      newException = await specialtyGroupExceptionsService.updateSpecialtyGroupException(state.project.projectId, exception);
    } else {
      //create
      newException = await specialtyGroupExceptionsService.createSpecialtyGroupException(state.project.projectId, exception);
    }

    const newRunConfiguration = structuredClone(state.runConfiguration);
    newRunConfiguration.hasUnprocessedSpecialtyGroupExceptions = newRunConfiguration.hasUnprocessedSpecialtyGroupExceptions || newException.isUnprocessed;

    const newProject = structuredClone(state.project);
    newProject.hasUnprocessedSpecialtyGroupExceptions = newRunConfiguration.hasUnprocessedSpecialtyGroupExceptions;

    // update local exception storage (county projects did this on the run config result)
    newTimesliceExceptions[exceptionKey] = newException;
    const newSpecialtyExceptionsMap = new Map(state.specialtyExceptionsMap);
    newSpecialtyExceptionsMap.set(state.runConfiguration.projectRunConfigurationId, newTimesliceExceptions);

    // dispatch project and exception changes
    dispatch({
      payload: {
        project: newProject,
        runConfiguration: newRunConfiguration,
        specialtyExceptionsMap: newSpecialtyExceptionsMap,
      },
      type: ZipProjectActionsEnum.SubmitSpecialtyExceptions,
    });
  }

  const onNcpProjectRunStatusChange = async (notificationPayload?: ZipCodeAnalysisNcpStatusChangeNotificationMessagePayload) => {
    let newStatus = state.ncpProjectRunStatus;
    if (notificationPayload.projectRunStatus as unknown as ProjectRunStatusEnum === ProjectRunStatusEnum.Running) {
      // the expectation is for notifications with Running to include the expected number of jobs,
      // and for notifications with Succeeded to not. ncpJobsExpected should only be set once.
      ncpJobsExpected.current = notificationPayload.numberOfJobs ?? 0;
      ncpJobsCompleted.current = 0;
      newStatus = notificationPayload.projectRunStatus;
    } else if (notificationPayload.projectRunStatus === ProjectRunConfigurationStatus.Succeeded) {
      ncpJobsCompleted.current += 1;
      if (ncpJobsCompleted.current === ncpJobsExpected.current) {
        newStatus = notificationPayload.projectRunStatus;
      }
    }

    dispatch({
      payload: {
        ncpProjectRunStatus: newStatus,
      },
      type: ZipProjectActionsEnum.ChangeNcpProjectRunStatus,
    });
  }

  return (
    <ZipProjectContext.Provider
      value={{
        dispatch,
        getLatestRunConfiguration,
        getSearchParametersFromFilterSelections,
        onApplyFilters,
        onChangeChangedGapsFilter,
        onChangeCompareToRunConfiguration,
        onChangeExcludeZeroMembership,
        onChangePerspectiveToZipClass,
        onChangePerspectiveToZipCode,
        onChangeProjectStatus,
        onChangeRunConfiguration,
        onChangeSelectedMemberships,
        onChangeTablePage,
        onClickSpecialtyGroupOrZipCode,
        onEditTimeslicePublishComment,
        onFinishProcessingException,
        onLoadPage,
        onNcpProjectRunStatusChange,
        onProcessProviderChanges,
        onProcessSpecialtyExceptions,
        onPublishTimeSlice,
        onResetFilters,
        onRunProject,
        onSortTable,
        onSubmitEditProject,
        onSubmitSpecialtyException,
        onUpdateUnprocessedProjectionProviders,
        onUpdateUnprocessedProviders,
        onUpdateUnprocessedTerminations,
        state,
      }}>
      {props.children}
    </ZipProjectContext.Provider>
  )
};

export function withZipContextState<T>(Component: ComponentType<PropsWithChildren<T & ZipContextState>>): ComponentType<PropsWithChildren<T>> {
  class WithZipProjectContext extends PureComponent<T> {
    public render() {
      return (
        <ZipProjectContext.Consumer>
          {(context) => <Component {...this.props} {...context.state} />}
        </ZipProjectContext.Consumer>
      );
    }
  }

  return WithZipProjectContext;
}

export function withZipContextEventHandlers<T>(Component: ComponentType<PropsWithChildren<T & ZipContextEventHandlersObject>>): ComponentType<PropsWithChildren<T>> {
  class WithZipProjectContextEventHandlers extends PureComponent<T> {
    public render() {
      return (
        <ZipProjectContext.Consumer>
          {(context) => (
            <Component
              {...this.props}
              zipContextEvents={{
                onApplyFilters: context.onApplyFilters,
                onChangeChangedGapsFilter: context.onChangeChangedGapsFilter,
                onChangeCompareToRunConfiguration: context.onChangeCompareToRunConfiguration,
                onChangeExcludeZeroMembership: context.onChangeExcludeZeroMembership,
                onChangePerspectiveToZipClass: context.onChangePerspectiveToZipClass,
                onChangePerspectiveToZipCode: context.onChangePerspectiveToZipCode,
                onChangeProjectStatus: context.onChangeProjectStatus,
                onChangeRunConfiguration: context.onChangeRunConfiguration,
                onChangeSelectedMemberships: context.onChangeSelectedMemberships,
                onChangeTablePage: context.onChangeTablePage,
                onClickSpecialtyGroupOrZipCode: context.onClickSpecialtyGroupOrZipCode,
                onEditTimeslicePublishComment: context.onEditTimeslicePublishComment,
                onFinishProcessingException: context.onFinishProcessingException,
                onLoadPage: context.onLoadPage,
                onNcpProjectRunStatusChange: context.onNcpProjectRunStatusChange,
                onProcessProviderChanges: context.onProcessProviderChanges,
                onProcessSpecialtyExceptions: context.onProcessSpecialtyExceptions,
                onPublishTimeSlice: context.onPublishTimeSlice,
                onResetFilters: context.onResetFilters,
                onRunProject: context.onRunProject,
                onSortTable: context.onSortTable,
                onSubmitEditProject: context.onSubmitEditProject,
                onSubmitSpecialtyException: context.onSubmitSpecialtyException,
                onUpdateUnprocessedProjectionProviders: context.onUpdateUnprocessedProjectionProviders,
                onUpdateUnprocessedProviders: context.onUpdateUnprocessedProviders,
                onUpdateUnprocessedTerminations: context.onUpdateUnprocessedTerminations
              }}
            />
          )}
        </ZipProjectContext.Consumer>
      )
    }
  }

  return WithZipProjectContextEventHandlers;
}
